/**
 * AdminProducts.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clearAssetIdCache } from '../utils/assetImageMapping';
import { getIcon } from '../utils/icons';
import AsyncProductImage from './AsyncProductImage';
import ProductSearch from './ProductSearch';
import BundleProductPicker from './BundleProductPicker';
import TuneIndexPickerModal from './TuneIndexPickerModal';
import VariantConfigurator from './VariantConfigurator';
import HelpPopup from './HelpPopup';
import LoadingSpinner from './LoadingSpinner';
import { queryMatchesSearchText, productRowSearchHaystack } from '../utils/siteSearch';
import { parseContentDispositionFilename } from '../utils/contentDispositionFilename';
import { mergeTagLists, parseTagList, suggestProductTags } from '../utils/autoPopulateTags';
import './AdminProducts.css';

const API_URL = '/api/products';
/** Admin list includes hidden-from-shop products; public GET /api/products does not. */
const ADMIN_PRODUCTS_LIST_URL = '/api/admin/products';
const IMAGES_PATH = '/images/products/';

function parseJsonSafe(text) {
  if (text == null || text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Book/CD tune index: `content_pages.page_id` (e.g. stubbert3rd-index). Accepts slug or full `/page/…` URL. */
function normalizeIndexPageIdInput(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const m = s.match(/\/page\/([^/?#]+)/i);
  if (m) return m[1].trim();
  return s.replace(/^\/+/, '');
}

/** User-facing copy for failed POST/PUT /api/products */
function productSaveFailureMessage(status, rawText) {
  const body = parseJsonSafe(rawText);
  const apiMsg = body && (body.error || body.message);
  const details = body && body.details;
  const raw = (rawText && rawText.trim()) || '';

  if (status === 401) {
    return {
      summary: 'Your session has expired or you are not signed in.',
      hint: 'Open /login, sign in with an admin-capable account, then return to Admin → Products and try again.'
    };
  }
  if (status === 403) {
    return {
      summary: 'You do not have permission to save products.',
      hint: 'Your account needs products.edit (or an admin role). Ask an administrator to update your permissions.'
    };
  }
  if (status === 400) {
    return {
      summary: apiMsg || 'The server rejected this product (missing or invalid fields).',
      hint: 'Confirm Basic Info has a name and Pricing has a valid price (0 for free is allowed) and an inventory quantity. The API requires name and price_cents.'
    };
  }
  if (status === 404) {
    return {
      summary: apiMsg || 'The products API was not found.',
      hint: 'For local development, ensure the backend is running and that the React dev server proxies /api to it (see frontend/src/setupProxy.js, usually port 4000).'
    };
  }

  let summary = apiMsg || (raw.length > 0 ? raw.slice(0, 400) : `Something went wrong (HTTP ${status || '?'}).`);
  if (details) summary = `${summary} ${details}`;

  const hints = [];
  if (/UNIQUE|unique constraint|UNIQUE failed/i.test(raw) || /UNIQUE|unique/i.test(summary)) {
    hints.push('This often means the SKU is already in use. Use Generate next to SKU or enter a different code.');
  }
  if (/no such column|SQLITE_ERROR/i.test(raw) || (apiMsg && /migration|columns missing/i.test(String(apiMsg)))) {
    hints.push('The database schema may be out of date. Check the API terminal log and run any migration the message refers to.');
  }
  if (status >= 500 || status === 0 || !status) {
    hints.push('Check the terminal where the API server runs for the full error. Confirm the API process is up after code or database changes.');
  }

  return {
    summary,
    hint: hints.join(' ')
  };
}

// Category icon mapping function
const getCategoryIcon = (product) => {
  // Check if product has a category field (preferred)
  const categoryName = product.category || product.type;

  if (!categoryName) {
    return getIcon('bundle');
  }

  const category = categoryName.toLowerCase();

  // Map category names to icons - supports all database categories
  if (category === 'book' || category.includes('book')) {
    return '📖';
  } else if (category === 'cd' || category.includes('cd')) {
    return '💿';
  } else if (category === 'bundle' || category.includes('bundle')) {
    return '📦';
  } else if (category === 'video' || category.includes('video')) {
    return '🎬';
  } else if (category === 'mp3' || category.includes('mp3')) {
    return '🎵';
  } else if (category === 'coffee' || category.includes('coffee')) {
    return '☕';
  } else if (category === 'sheet music' || category.includes('sheet music')) {
    return '🎼';
  } else if (category === 'merchandise' || category.includes('merchandise')) {
    return '🎁';
  } else if (category === 'accessories' || category.includes('accessories') || category.includes('accessory')) {
    return '🎸';
  } else if (category === 'digital download' || category.includes('digital download') || category.includes('download')) {
    return '📥';
  } else if (category.includes('pipe music')) {
    return getIcon('music');
  } else if (category.includes('celtic music')) {
    return getIcon('music');
  } else if (category.includes('irish')) {
    return '🍀';
  } else if (category.includes('scottish')) {
    return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
  } else if (category.includes('canadian')) {
    return '🍁';
  } else if (category.includes('cape breton')) {
    return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
  }

  // Default fallback
  return '📦';
};

// Helper to fetch available images from the public/images/products folder
async function fetchAvailableImages() {
  // Comprehensive list of available product images organized by category
  return [
    // Cape Breton Books
    'cape_breton/Book-BrendaStubbert.jpg',
    'cape_breton/Book-JerryHolland1.jpg',
    'cape_breton/Book-JerryHolland2.jpg',
    'cape_breton/Book-Lighthouse.jpg',
    'cape_breton/Book-Winston.jpg',
    'cape_breton/book-HeatherHill.jpg',
    'cape_breton/Book-TripToWindsor.jpg',
    'cape_breton/book-alistermacgillivray.jpg',
    'cape_breton/Kinnon105.JPG',
    'cape_breton/Andrea-Tunes.jpg',
    'cape_breton/Fiddletree.jpg',
    'cape_breton/CelticColours.jpg',
    'cape_breton/Campbell_Legacy.jpg',
    'cape_breton/CBFC_Cover.jpg',
    'cape_breton/CBHC.JPG',
    'cape_breton/CBSC.jpg',
    'cape_breton/book-barryshearsvol1.jpg',
    'cape_breton/stubbert2nd.jpg',
    'cape_breton/BookCD_Winston.jpg',
    'cape_breton/Holland1FC.jpg',

    // Irish Books
    'irish/Book-Allans.jpg',
    'irish/Book-SeanRyan.jpg',
    'irish/Book-Ryans.jpg',
    'irish/book-LanganCover.GIF',
    'irish/book-PackieManus.jpg',
    'irish/Book-MusicalMemories.jpg',
    'irish/bk-EssentialFlute.jpg',
    'irish/HandyStick.JPG',
    'irish/turoeStone.JPG',
    'irish/Miller-Perron.jpg',

    // Scottish Books
    'scottish/Book-Skye.jpg',
    'scottish/Book-SimonFraser.jpg',
    'scottish/Book-Skinner.jpg',
    'scottish/book-harpclaymore.jpg',
    'scottish/Marshall.jpg',
    'scottish/glengarry.JPG',

    // Canadian Books
    'canadian/book-CFM1.jpg',

    // Pipe Music
    'pipe_music/book-barryshearsvol1.jpg',

    // CDs
    'cds/BookCD_Winston.jpg',
    'cds/Holland1FC.jpg',

    // Placeholder
    'placeholder.jpg'
  ];
}

function slugifyDigitalBundle(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'album';
}

const AdminProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState({
    id: '',
    name: '',
    type: 'Book',
    price: '',
    inventory: '',
    description: '',
    image: '',
    author: '',
    artist: '',
    category: '',
    sku: '',
    shipping_enabled: true,
    minimum_shipping: '',
    minimum_shipping_international: '',
    weight_kg: '',
    wholesale_pricing: {
      standard: { min_qty: 5, discount: 20 },
      premium: { min_qty: 10, discount: 30 },
      volume: { min_qty: 25, discount: 40 }
    },
    tax_type: 'auto', // 'auto', 'gst', 'hst', 'exempt'
    tax_rate: '', // Custom tax rate percentage (e.g., 5, 13, 15)
    tax_notes: '', // Notes about why this tax configuration is set
    // Bundle configuration
    is_bundle: false, // Whether this product is a bundle
    bundle_products: '', // Comma-separated product IDs that can be selected in this bundle
    bundle_min_quantity: 5, // Minimum quantity to get bundle discount
    bundle_discount: 30, // Discount percentage when min quantity reached
    bundle_max_selections: 10, // Maximum number of products customer can select
    bundle_fixed_price_cents: null, // Fixed price for bundle (in cents) - alternative to percentage discount
    featured: false, // Whether this product is featured (shows first on shop page)
    hide_from_shop: false, // Omit from /shop grid; still available via direct product URL
    checkout_promo: false, // Auto-add as $0 gift when cart has physical paid items (not digital-only)
    source_url: '', // Original source URL from cranfordpub.com
    sample_tunes_limit: 9, // Max sample tune cards on public product page
    index_page_id: '', // content_pages.page_id for tune index (book-index / cd-index)
    bundle_options: [], // Array of fixed price bundle options
    is_digital_album: false,
    digital_bundle_slug: '',
    related_digital_product_id: '',
    digital_bundle_asset_id: '',
    digital_bundle_asset_name: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [productSaveError, setProductSaveError] = useState(null);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const fileInputRef = useRef(null);
  const [availableImages, setAvailableImages] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);
  const [, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('update');
  const importFileInputRef = useRef(null);
  const [showZIPImportModal, setShowZIPImportModal] = useState(false);
  const zipImportFileInputRef = useRef(null);
  const digitalZipInputRef = useRef(null);
  const [digitalZipUploading, setDigitalZipUploading] = useState(false);
  const [digitalZipUploadMessage, setDigitalZipUploadMessage] = useState(null);
  const [importProgress, setImportProgress] = useState({
    isLoading: false,
    step: '',
    message: '',
    error: null,
    productData: null,
    existingProduct: null
  });
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictResolution, setConflictResolution] = useState(null); // 'update' or 'create_new'
  const pendingImportFile = useRef(null); // Store file for conflict resolution

  // New states for search and multi-select
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);

  const digitalAlbumOptions = useMemo(
    () =>
      products.filter(
        (p) =>
          (p.is_digital_album === 1 || p.is_digital_album === true) &&
          (!formState.id || String(p.id) !== String(formState.id))
      ),
    [products, formState.id]
  );

  const handleDigitalAlbumToggle = (checked) => {
    setFormState((prev) => ({
      ...prev,
      is_digital_album: checked,
      shipping_enabled: checked ? false : prev.shipping_enabled,
      type: checked ? 'digital download' : prev.type,
      tax_type: checked ? 'hst' : prev.tax_type,
      digital_bundle_slug:
        checked && !prev.digital_bundle_slug ? slugifyDigitalBundle(prev.name) : prev.digital_bundle_slug,
      related_digital_product_id: checked ? '' : prev.related_digital_product_id,
      digital_bundle_asset_id: checked ? prev.digital_bundle_asset_id : '',
      digital_bundle_asset_name: checked ? prev.digital_bundle_asset_name : '',
    }));
  };

  const handleDigitalZipUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!getToken()) {
      setDigitalZipUploadMessage({ type: 'error', text: 'Sign in as admin to upload.' });
      return;
    }

    setDigitalZipUploading(true);
    setDigitalZipUploadMessage(null);

    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch('/api/digital/upload-bundle', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      setFormState((prev) => ({
        ...prev,
        is_digital_album: true,
        shipping_enabled: false,
        type: prev.type === 'Book' ? 'digital download' : prev.type,
        tax_type: 'hst',
        digital_bundle_asset_id: String(data.assetId),
        digital_bundle_asset_name: data.filename || file.name,
        digital_bundle_slug: prev.digital_bundle_slug || slugifyDigitalBundle(prev.name || file.name),
      }));
      setDigitalZipUploadMessage({
        type: 'success',
        text: data.message || `Uploaded ${file.name}`,
        tracks: data.summary?.tracks,
      });
    } catch (err) {
      setDigitalZipUploadMessage({ type: 'error', text: err.message || 'Upload failed' });
    } finally {
      setDigitalZipUploading(false);
    }
  };

  // Duplicate checking states
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateProducts, setDuplicateProducts] = useState([]);

  // Categories state
  const [categories, setCategories] = useState([]);

  // Bundle product picker modal state
  const [showBundlePickerModal, setShowBundlePickerModal] = useState(false);
  const [showTuneIndexPicker, setShowTuneIndexPicker] = useState(false);

  // Debug: Track categories state changes
  useEffect(() => {
    console.log('🔍 CATEGORIES STATE CHANGED:', {
      count: categories.length,
      categories: categories.map(c => ({ id: c.id, name: c.name, icon: c.icon }))
    });
  }, [categories]);

  // Compute categories to use in dropdown (with fallback)
  const categoriesToUse = useMemo(() => {
    const fallbackCategories = [
      { id: 'f1', name: 'Book', icon: '📖' },
      { id: 'f2', name: 'CD', icon: '💿' },
      { id: 'f3', name: 'Bundle', icon: '📦' },
      { id: 'f4', name: 'Video', icon: '🎬' }
    ];

    const result = categories && categories.length > 0 ? categories : fallbackCategories;
    console.log('🧮 useMemo: Computing categoriesToUse, length:', result.length);
    return result;
  }, [categories]);

  // Helper function to get fresh token
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Generate unique product number
  const generateProductNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `CP-${timestamp}-${random}`;
  };

  // Auto-populate tags based on product metadata (merges with existing tags)
  const generateTags = () => {
    const existing = parseTagList(formState.tags);
    const suggested = suggestProductTags({
      name: formState.name,
      description: formState.description,
      category: formState.type,
      type: formState.type,
      author: formState.author,
      artist: formState.artist,
      sku: formState.sku,
      seo_title: formState.seo_title,
      seo_description: formState.seo_description,
      index_page_id: formState.index_page_id,
      is_bundle: formState.is_bundle,
      is_digital_album: formState.is_digital_album,
    });
    const merged = mergeTagLists(existing, suggested);
    const addedCount = merged.length - existing.length;

    setFormState(prev => ({ ...prev, tags: merged.join(', ') }));

    if (addedCount > 0) {
      alert(`✓ Added ${addedCount} tag suggestion(s) (${merged.length} total). Existing tags kept.`);
    } else if (merged.length > 0) {
      alert(`ℹ️ No new tags to add — ${merged.length} tag(s) already cover this product.`);
    } else {
      alert('ℹ️ Could not suggest tags. Add title, description, category, or author/artist first.');
    }
  };

  // Multi-select handlers
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
  };

  // Duplicate products handler
  const handleDuplicateSelected = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to duplicate.');
      return;
    }

    const confirmMessage = `Are you sure you want to duplicate ${selectedProducts.length} product(s)?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      const productsToDuplicate = products.filter(p => selectedProducts.includes(p.id));

      for (const product of productsToDuplicate) {
        const duplicateData = {
          name: `${product.name} (Copy)`,
          description: product.description,
          price_cents: product.price_cents,
          image: product.image,
          author: product.author,
          artist: product.artist,
          category: product.category,
          inventory: product.inventory,
          sku: generateProductNumber(),
          wholesale_pricing: product.wholesale_pricing
        };

        await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + getToken()
          },
          body: JSON.stringify(duplicateData),
        });
      }

      // Refresh products list
      const response = await fetch(`${ADMIN_PRODUCTS_LIST_URL}?t=${new Date().getTime()}`, {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Cache-Control': 'no-cache'
        }
      });
      const data = await response.json();
      setProducts(data);

      // Clear selection
      setSelectedProducts([]);

      alert(`Successfully duplicated ${selectedProducts.length} product(s)!`);
    } catch (error) {
      console.error('Error duplicating products:', error);
      alert('Failed to duplicate products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete multiple products handler
  const handleDeleteSelected = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to delete.');
      return;
    }

    const confirmMessage = `⚠️ WARNING: Are you sure you want to delete ${selectedProducts.length} product(s)?\n\nThis action cannot be undone!`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Double confirmation for safety
    const doubleConfirm = window.confirm(`This is your final confirmation. Delete ${selectedProducts.length} product(s)?`);
    if (!doubleConfirm) {
      return;
    }

    setLoading(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      // Delete each selected product
      for (const productId of selectedProducts) {
        try {
          const response = await fetch(`${API_URL}/${productId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': 'Bearer ' + getToken()
            }
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to delete product ${productId}`);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error deleting product ${productId}:`, error);
        }
      }

      // Update products list by removing deleted items
      setProducts(prevProducts =>
        prevProducts.filter(product => !selectedProducts.includes(product.id))
      );

      // Clear selection
      setSelectedProducts([]);

      // Show result message
      if (errorCount === 0) {
        alert(`✓ Successfully deleted ${successCount} product(s)!`);
      } else {
        alert(`Deleted ${successCount} product(s). Failed to delete ${errorCount} product(s).`);
      }
    } catch (error) {
      console.error('Error deleting products:', error);
      alert('Failed to delete products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search term (same rules as shop / Advanced Search)
  const filteredProducts = products.filter(product =>
    queryMatchesSearchText(productRowSearchHaystack(product), searchTerm, { title: product.name }),
  );

  // Tab switching handler
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    // Load assets when switching to image tab
    if (tabName === 'image') {
      searchAssets('');
    }
  };

  // Search assets from database
  const searchAssets = async (searchTerm = '') => {
    setAssetSearchLoading(true);
    try {
      const response = await fetch(`/api/assets?file_type=image&search=${encodeURIComponent(searchTerm)}&limit=50`);
      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
      } else {
        console.error('Failed to fetch assets');
        setAssets([]);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    } finally {
      setAssetSearchLoading(false);
    }
  };

  // Handle asset search input change
  const handleAssetSearchChange = (e) => {
    const term = e.target.value;
    setAssetSearchTerm(term);
    // Debounce search
    clearTimeout(window.assetSearchTimeout);
    window.assetSearchTimeout = setTimeout(() => {
      searchAssets(term);
    }, 300);
  };

  // Handle asset selection
  const handleAssetSelect = (asset) => {
    console.log('Asset selected:', asset);
    // Store the asset reference as /api/assets/{id}/download (with leading slash)
    const assetPath = `/api/assets/${asset.id}/download`;
    setFormState(prev => {
      const newState = {
        ...prev,
        image: assetPath
      };
      console.log('Form state updated with image:', newState.image);
      console.log('Asset path:', assetPath);
      return newState;
    });
    // Clear any upload preview when selecting from assets
    setImagePreview(null);
    console.log('Asset selection complete, formState.image should be:', assetPath);
  };

  // Handle opening add product modal
  const handleAddProduct = () => {
    setFormState({
      id: '',
      name: '',
      type: 'Book',
      price: '',
      inventory: '',
      description: '',
      image: '',
      author: '',
      artist: '',
      category: '',
      sku: '',
      tags: '',
      wholesale_pricing: {
        standard: { min_qty: 5, discount: 20 },
        premium: { min_qty: 10, discount: 30 },
        volume: { min_qty: 25, discount: 40 }
      },
      is_bundle: false,
      bundle_products: '',
      bundle_min_quantity: 5,
      bundle_discount: 30,
      bundle_max_selections: 10,
      bundle_fixed_price_cents: null,
      bundle_options: [],
      featured: false,
      hide_from_shop: false,
      checkout_promo: false,
      source_url: '',
      sample_tunes_limit: 9,
      index_page_id: '',
      is_digital_album: false,
      digital_bundle_slug: '',
      related_digital_product_id: '',
      digital_bundle_asset_id: '',
      digital_bundle_asset_name: ''
    });
    setImagePreview(null); // Clear any previous preview
    setActiveTab('basic');
    setProductSaveError(null);
    setIsAdding(true);
  };

  // Fetch products from backend
  useEffect(() => {
    setLoading(true);
    // Add cache-busting parameter to prevent stale data
    const cacheBuster = new Date().getTime();
    fetch(`${ADMIN_PRODUCTS_LIST_URL}?t=${cacheBuster}`, {
      headers: {
        'Authorization': 'Bearer ' + getToken(),
        'Cache-Control': 'no-cache'
      }
    })
      .then(res => {
        console.log('AdminProducts: API response status:', res.status);
        return res.json();
      })
      .then(data => {
        console.log('AdminProducts: Products loaded from API:', data);
        setProducts(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('AdminProducts: Error fetching products:', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchAvailableImages().then(setAvailableImages);
    fetchCategories();
  }, []);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      console.log('📂 Fetching categories from API...');
      const response = await fetch('/api/categories');
      console.log('📂 Categories response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('📂 Categories loaded from API:', {
          count: data.length,
          categories: data.map(c => `${c.icon} ${c.name} (id:${c.id})`)
        });
        setCategories(data);
        console.log('📂 setCategories() called with', data.length, 'categories');
      } else {
        console.error('❌ Failed to fetch categories:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
    }
  };

  // Check for edit query parameter and auto-open edit modal
  useEffect(() => {
    const editProductId = searchParams.get('edit');
    if (editProductId && products.length > 0 && !isEditing) {
      const productToEdit = products.find(p => p.id === parseInt(editProductId));
      if (productToEdit) {
        handleEdit(productToEdit);
        // Clear the query parameter after opening edit modal
        searchParams.delete('edit');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, products, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prevState => ({ ...prevState, [name]: value }));
  };

  const handleWholesaleChange = (tier, field, value) => {
    setFormState(prevState => ({
      ...prevState,
      wholesale_pricing: {
        ...prevState.wholesale_pricing,
        [tier]: {
          ...prevState.wholesale_pricing[tier],
          [field]: value
        }
      }
    }));
  };

  // Check for duplicate products
  const checkForDuplicates = (productData, currentProductId = null) => {
    const duplicates = [];
    const nameLower = productData.name.toLowerCase().trim();
    const skuLower = productData.sku ? productData.sku.toLowerCase().trim() : '';

    products.forEach(product => {
      // Skip if checking against the same product (during edit)
      // Convert both to numbers to handle string/number type mismatches
      if (currentProductId && parseInt(product.id) === parseInt(currentProductId)) {
        return;
      }

      const reasons = [];

      // Check for exact name match
      if (product.name.toLowerCase().trim() === nameLower) {
        reasons.push('Exact name match');
      }

      // Check for similar name (>95% similar)
      const similarity = calculateSimilarity(product.name.toLowerCase(), nameLower);
      if (similarity > 0.95 && similarity < 1.0) {
        reasons.push(`Similar name (${Math.round(similarity * 100)}% match)`);
      }

      // Check for SKU match
      if (skuLower && product.sku && product.sku.toLowerCase().trim() === skuLower) {
        reasons.push('Same SKU');
      }

      // Check for exact price and category match (possible duplicate)
      // Removed as it causes too many false positives for items with same price
      /*
      if (product.price_cents === productData.price_cents &&
        product.category === productData.category &&
        reasons.length === 0) {
        reasons.push('Same price and category');
      }
      */

      if (reasons.length > 0) {
        duplicates.push({
          product,
          reasons
        });
      }
    });

    return duplicates;
  };

  // Simple string similarity calculation (Dice coefficient)
  const calculateSimilarity = (str1, str2) => {
    const bigrams1 = getBigrams(str1);
    const bigrams2 = getBigrams(str2);
    const intersection = bigrams1.filter(x => bigrams2.includes(x));
    return (2.0 * intersection.length) / (bigrams1.length + bigrams2.length);
  };

  const getBigrams = (str) => {
    const bigrams = [];
    for (let i = 0; i < str.length - 1; i++) {
      bigrams.push(str.substring(i, i + 2));
    }
    return bigrams;
  };

  // Handle proceeding despite duplicate warning
  const handleProceedWithSave = async () => {
    setProductSaveError(null);
    setShowDuplicateWarning(false);
    setDuplicateProducts([]);
    await saveProduct();
  };

  const handleCancelDuplicateWarning = () => {
    setShowDuplicateWarning(false);
    setDuplicateProducts([]);
  };

  // Bundle Options Handlers
  const addBundleOption = () => {
    setFormState(prev => ({
      ...prev,
      bundle_options: [...(prev.bundle_options || []), { name: '', price: '', items: [] }]
    }));
  };

  const removeBundleOption = (index) => {
    setFormState(prev => ({
      ...prev,
      bundle_options: (prev.bundle_options || []).filter((_, i) => i !== index)
    }));
  };

  const updateBundleOption = (index, field, value) => {
    setFormState(prev => {
      const newOptions = [...(prev.bundle_options || [])];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return { ...prev, bundle_options: newOptions };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields manually (since HTML5 validation doesn't work with tabs)
    if (!formState.name || formState.name.trim() === '') {
      setProductSaveError({
        summary: 'Product name is required.',
        hint: 'Open the Basic Info tab and enter a name before saving.'
      });
      setActiveTab('basic');
      return;
    }

    const priceRaw = formState.price;
    if (priceRaw === '' || priceRaw === null || priceRaw === undefined) {
      setProductSaveError({
        summary: 'Price is required.',
        hint: 'Open the Pricing tab. Use 0 for a free item, or enter dollars such as 19.99. Amounts are stored as cents on the server.'
      });
      setActiveTab('pricing');
      return;
    }
    const priceNum = parseFloat(priceRaw);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      setProductSaveError({
        summary: 'Enter a valid price (zero or greater).',
        hint: 'Open the Pricing tab. Negative prices are not allowed; use 0.00 for free.'
      });
      setActiveTab('pricing');
      return;
    }

    if (formState.inventory === '' || formState.inventory === null || formState.inventory === undefined) {
      setProductSaveError({
        summary: 'Inventory quantity is required.',
        hint: 'Open the Pricing tab and enter stock quantity (use 0 if out of stock).'
      });
      setActiveTab('pricing');
      return;
    }

    // Prepare product data for duplicate check
    const productDataForCheck = {
      name: formState.name,
      sku: formState.sku || generateProductNumber(),
      price_cents: Math.round(formState.price * 100),
      category: formState.type
    };

    // Check for duplicates before saving (skip if we're editing the same product)
    const duplicates = checkForDuplicates(productDataForCheck, isEditing ? formState.id : null);

    if (duplicates.length > 0) {
      // Show duplicate warning modal
      setDuplicateProducts(duplicates);
      setShowDuplicateWarning(true);
      return; // Stop submission until user confirms
    }

    if (formState.is_digital_album && !formState.digital_bundle_asset_id && !(formState.digital_bundle_slug || '').trim()) {
      setProductSaveError({
        summary: 'Digital album needs a master ZIP upload or a bundle slug.',
        hint: 'Upload a ZIP of MP3s (cover optional inside the ZIP), or set a legacy server folder slug.',
      });
      setActiveTab('basic');
      return;
    }

    // If no duplicates, proceed with save
    await saveProduct();
  };



  const saveProduct = async () => {
    if (!getToken()) {
      setProductSaveError({
        summary: 'You must be signed in to save products.',
        hint: 'Your session token is missing. Sign in at /login with an admin account, then open Admin → Products again.'
      });
      return;
    }

    setProductSaveError(null);
    setIsSavingProduct(true);

    // Handle image path construction properly
    let imagePath = '';
    if (formState.image) {
      console.log('Processing image in form submission:', {
        originalImage: formState.image,
        startsWithSlash: formState.image.startsWith('/'),
        startsWithHttp: formState.image.startsWith('http'),
        isAssetPath: formState.image.startsWith('api/assets/'),
        isTimestamp: formState.image.match(/^\d{13}-[a-z0-9]+-/),
        isUUID: formState.image.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/),
        hasUploads: formState.image.includes('uploads/')
      });

      const assetRef = formState.image.match(/\/api\/assets\/(\d+)\/(?:download|thumbnail|view)/);
      const staticAssetRef = formState.image.split('?')[0].match(/\/assets\/.+_(\d{3,})\.[a-z0-9]+$/i);
      if (assetRef) {
        imagePath = `/api/assets/${assetRef[1]}/download`;
        console.log('Using as asset API path:', imagePath);
      } else if (staticAssetRef) {
        imagePath = `/api/assets/${staticAssetRef[1]}/download`;
        console.log('Using as asset API path from static rewrite:', imagePath);
      }
      // Check if it's already a full path
      else if (formState.image.startsWith('/') || formState.image.startsWith('http')) {
        imagePath = formState.image;
        console.log('Using as full path:', imagePath);
      }
      // Check if it's a database asset filename (has timestamp prefix, file pattern, or UUID-like format)
      else if (formState.image.match(/^\d{13}-[a-z0-9]+-/) ||
        formState.image.match(/^file-\d+-[a-z0-9]+\./) ||
        formState.image.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/) ||
        formState.image.includes('uploads/')) {
        // For database assets, store the filename as-is
        imagePath = formState.image;
        console.log('Using as database asset filename:', imagePath);
      }
      // Otherwise, it's a legacy static image, prepend the images path
      else {
        imagePath = IMAGES_PATH + formState.image;
        console.log('Using as legacy static image:', imagePath);
      }
    }
    console.log('Final image path for submission:', imagePath);

    const productData = {
      name: formState.name,
      description: formState.description,
      price_cents: Math.round(formState.price * 100),
      image: imagePath,
      author: formState.author,
      artist: formState.artist,
      category: formState.type,
      inventory: formState.inventory ? parseInt(formState.inventory) : 20,
      sku: formState.sku || generateProductNumber(),
      shipping_enabled: formState.shipping_enabled,
      minimum_shipping_cents: formState.minimum_shipping ? Math.round(parseFloat(formState.minimum_shipping) * 100) : 0,
      minimum_shipping_international_cents: formState.minimum_shipping_international ? Math.round(parseFloat(formState.minimum_shipping_international) * 100) : 0,
      weight_kg: formState.weight_kg ? parseFloat(formState.weight_kg) : null,
      tags: formState.tags || '',
      related_products: formState.related_products || '',
      related_include_explicit: formState.related_include_explicit !== false,
      related_include_tags: formState.related_include_tags !== false,
      related_include_category: formState.related_include_category !== false,
      wholesale_pricing: formState.wholesale_pricing || {
        standard: { min_qty: 5, discount: 20 },
        premium: { min_qty: 10, discount: 30 },
        volume: { min_qty: 25, discount: 40 }
      },
      // Tax configuration
      tax_type: formState.tax_type || 'auto',
      tax_rate: formState.tax_rate ? parseFloat(formState.tax_rate) : null,
      tax_notes: formState.tax_notes || '',
      // Bundle configuration
      is_bundle: formState.is_bundle || false,
      bundle_products: formState.bundle_products || '',
      bundle_min_quantity: formState.bundle_min_quantity ? parseInt(formState.bundle_min_quantity) : null,
      bundle_discount: formState.bundle_discount ? parseFloat(formState.bundle_discount) : null,
      bundle_max_selections: formState.bundle_max_selections ? parseInt(formState.bundle_max_selections) : null,
      bundle_fixed_price_cents: formState.bundle_fixed_price_cents ? parseInt(formState.bundle_fixed_price_cents) : null,
      // Featured & checkout promo (explicit booleans; always sent on save)
      featured: !!formState.featured,
      hide_from_shop: !!formState.hide_from_shop,
      checkout_promo: !!formState.checkout_promo,
      // Source URL
      source_url: formState.source_url || '',
      sample_tunes_limit: Math.min(99, Math.max(1, parseInt(formState.sample_tunes_limit, 10) || 9)),
      index_page_id: normalizeIndexPageIdInput(formState.index_page_id),
      // Variants
      // Variants - Ensure has_variants is set if data exists
      has_variants: (formState.has_variants === 1 || formState.has_variants === true || (formState.variants_data && formState.variants_data !== 'null' && formState.variants_data !== '{}')) ? 1 : 0,
      variant_type: formState.variant_type || null,
      variants_data: formState.variants_data || null,
      bundle_options: formState.bundle_options ? JSON.stringify(formState.bundle_options) : null,
      is_digital_album: !!formState.is_digital_album,
      digital_bundle_slug: formState.is_digital_album ? (formState.digital_bundle_slug || '').trim() || null : null,
      related_digital_product_id: formState.related_digital_product_id
        ? parseInt(formState.related_digital_product_id, 10) || null
        : null,
      digital_bundle_asset_id: formState.is_digital_album && formState.digital_bundle_asset_id
        ? parseInt(formState.digital_bundle_asset_id, 10) || null
        : null
    };




    try {
      if (isEditing) {
        const res = await fetch(`${API_URL}/${formState.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + getToken()
          },
          body: JSON.stringify(productData),
        });

        const responseText = await res.text();
        if (!res.ok) {
          console.error('AdminProducts: Error response:', res.status, responseText);
          setProductSaveError(productSaveFailureMessage(res.status, responseText));
          return;
        }

        const updated = parseJsonSafe(responseText);
        if (!updated || typeof updated !== 'object') {
          setProductSaveError({
            summary: 'The server response could not be read after updating.',
            hint: 'The product may still have saved. Refresh the page and check the product list. Inspect the Network tab for the PUT /api/products response body.'
          });
          return;
        }
        console.log('AdminProducts: Updated product:', updated);

        clearAssetIdCache();
        setIsEditing(false);
      } else {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + getToken()
          },
          body: JSON.stringify(productData),
        });

        const responseText = await res.text();
        if (!res.ok) {
          console.error('AdminProducts: Error response:', res.status, responseText);
          setProductSaveError(productSaveFailureMessage(res.status, responseText));
          return;
        }

        const created = parseJsonSafe(responseText);
        if (!created || typeof created !== 'object') {
          setProductSaveError({
            summary: 'The server response could not be read after creating the product.',
            hint: 'The product may still have been created. Refresh the page. Inspect the Network tab for the POST /api/products response.'
          });
          return;
        }
        console.log('AdminProducts: Created product:', created);

        clearAssetIdCache();
        setIsAdding(false);
      }

      const cacheBuster = new Date().getTime();
      fetch(`${ADMIN_PRODUCTS_LIST_URL}?t=${cacheBuster}`, {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache'
        }
      })
        .then((r) => {
          if (!r.ok) {
            console.warn('AdminProducts: Refresh list failed:', r.status);
            return null;
          }
          return r.json();
        })
        .then((data) => {
          if (data) setProducts(data);
        })
        .catch((e) => console.warn('AdminProducts: Refresh list error:', e));

      setFormState({
        id: '', name: '', type: 'Book', price: '', inventory: '', description: '', image: '', shipping_enabled: true, minimum_shipping: '', minimum_shipping_international: '', weight_kg: '',
        author: '', artist: '', category: '', sku: '', tags: '',
        wholesale_pricing: {
          standard: { min_qty: 5, discount: 20 },
          premium: { min_qty: 10, discount: 30 },
          volume: { min_qty: 25, discount: 40 }
        },
        tax_type: 'auto',
        tax_rate: '',
        tax_notes: '',
        is_bundle: false,
        bundle_products: '',
        bundle_min_quantity: 5,
        bundle_discount: 30,
        bundle_max_selections: 10,
        bundle_fixed_price_cents: null,
        featured: false,
        hide_from_shop: false,
        checkout_promo: false,
        source_url: '',
        sample_tunes_limit: 9,
        index_page_id: '',
        bundle_options: [],
        related_products: '',
        related_include_explicit: true,
        related_include_tags: true,
        related_include_category: true,
        has_variants: false,
        variant_type: '',
        variants_data: null,
        is_digital_album: false,
        digital_bundle_slug: '',
        related_digital_product_id: '',
        digital_bundle_asset_id: '',
        digital_bundle_asset_name: ''
      });
      setIsEditing(false);
      setProductSaveError(null);
      console.log('AdminProducts: Products after add/edit:', products);
    } catch (error) {
      console.error('AdminProducts: Error saving product:', error);
      const msg = error && error.message;
      setProductSaveError({
        summary: msg && msg.includes('Failed to fetch')
          ? 'Could not reach the API (network error).'
          : (msg || 'Something went wrong while saving.'),
        hint: msg && msg.includes('Failed to fetch')
          ? 'Confirm the backend is running and that this app proxies /api to it (e.g. localhost:4000). Check your connection and try again.'
          : 'See the browser console for details. Try signing out and back in if the problem continues.'
      });
    } finally {
      setIsSavingProduct(false);
    }
  };

  const handleEdit = async (product) => {
    setProductSaveError(null);
    // Refresh categories when opening edit form to ensure latest are available
    console.log('🔧 handleEdit: START - Refreshing categories before opening modal...');
    console.log('🔧 handleEdit: Categories BEFORE fetch:', categories.length);

    // Fetch categories and wait for state to update
    const response = await fetch('/api/categories');
    if (response.ok) {
      const freshCategories = await response.json();
      console.log('🔧 handleEdit: Fresh categories fetched:', freshCategories.length);
      setCategories(freshCategories);

      // Wait a moment for state to propagate
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log('🔧 handleEdit: AFTER fetch and state update');
    console.log('🔧 handleEdit: Categories AFTER fetch:', categories.length);

    // Handle image path properly when editing
    let imageName = '';
    if (product.image) {
      // If it's a database asset (has timestamp prefix, file pattern, UUID, or uploads path), use as-is
      if (product.image.match(/^\d{13}-[a-z0-9]+-/) ||
        product.image.match(/^file-\d+-[a-z0-9]+\./) ||
        product.image.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/) ||
        product.image.includes('uploads/') ||
        product.image.startsWith('http')) {
        imageName = product.image;
      }
      // If it's a legacy static image path, strip the IMAGES_PATH prefix
      else if (product.image.startsWith(IMAGES_PATH)) {
        imageName = product.image.replace(IMAGES_PATH, '');
      }
      // Otherwise, use as-is (might be just a filename)
      else {
        imageName = product.image;
      }
    }

    // Debug: Log incoming product data for bundle fields
    console.log('📦 handleEdit: Loading product with bundle data:', {
      id: product.id,
      name: product.name,
      is_bundle: product.is_bundle,
      bundle_products: product.bundle_products,
      bundle_min_quantity: product.bundle_min_quantity,
      bundle_discount: product.bundle_discount,
      bundle_max_selections: product.bundle_max_selections
    });

    setFormState({
      ...product,
      price: product.price_cents / 100,
      image: imageName,
      type: product.category || 'Book',
      author: product.author || '',
      artist: product.artist || '',
      sku: product.sku || '',
      tags: product.tags || '',
      shipping_enabled: product.shipping_enabled !== false && product.shipping_enabled !== 0,
      minimum_shipping: product.minimum_shipping_cents ? (product.minimum_shipping_cents / 100).toFixed(2) : '',
      minimum_shipping_international: product.minimum_shipping_international_cents ? (product.minimum_shipping_international_cents / 100).toFixed(2) : '',
      weight_kg: product.weight_kg ? product.weight_kg.toString() : '',
      wholesale_pricing: product.wholesale_pricing || {
        standard: { min_qty: 5, discount: 20 },
        premium: { min_qty: 10, discount: 30 },
        volume: { min_qty: 25, discount: 40 }
      },
      // Convert SQLite integer (0/1) to boolean
      is_bundle: product.is_bundle === 1 || product.is_bundle === true,
      bundle_products: product.bundle_products || '',
      bundle_min_quantity: product.bundle_min_quantity || 5,
      bundle_discount: product.bundle_discount || 30,
      bundle_max_selections: product.bundle_max_selections || 10,
      bundle_fixed_price_cents: product.bundle_fixed_price_cents || null,
      featured: product.featured === 1 || product.featured === true,
      hide_from_shop: product.hide_from_shop === 1 || product.hide_from_shop === true,
      checkout_promo: !!(product.checkout_promo === 1 || product.checkout_promo === true || product.checkout_promo === '1'),
      source_url: product.source_url || '',
      sample_tunes_limit: product.sample_tunes_limit != null && product.sample_tunes_limit !== ''
        ? parseInt(product.sample_tunes_limit, 10) || 9
        : 9,
      index_page_id: product.index_page_id || '',
      bundle_options: product.bundle_options ? JSON.parse(product.bundle_options) : [],
      is_digital_album: product.is_digital_album === 1 || product.is_digital_album === true,
      digital_bundle_slug: product.digital_bundle_slug || '',
      related_digital_product_id: product.related_digital_product_id
        ? String(product.related_digital_product_id)
        : '',
      digital_bundle_asset_id: product.digital_bundle_asset_id
        ? String(product.digital_bundle_asset_id)
        : '',
      digital_bundle_asset_name: product.digital_bundle_asset_id ? `Asset #${product.digital_bundle_asset_id}` : ''
    });

    console.log('📦 handleEdit: Form state set, is_bundle =', product.is_bundle === 1 || product.is_bundle === true);
    setImagePreview(null); // Clear any previous preview

    // Auto-switch to bundle tab if this is a bundle product
    if (product.is_bundle === 1 || product.is_bundle === true) {
      console.log('📦 handleEdit: This is a bundle product, switching to bundle tab');
      setActiveTab('bundle');
    } else {
      setActiveTab('basic');
    }

    console.log('🔧 handleEdit: About to open modal...');
    console.log('🔧 handleEdit: Final categories count before setIsEditing:', categories.length);
    setIsEditing(true);
    console.log('🔧 handleEdit: Modal opened (isEditing=true)');
  };

  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      setUploadingImage(true);

      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'products');
        formData.append('description', `Product image for ${formState.name || 'new product'}`);
        formData.append('alt_text', file.name);
        formData.append('is_public', 'true');

        // Upload to assets API
        const response = await fetch('/api/assets', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Image uploaded successfully:', result);

          const assetPath = result.id
            ? `/api/assets/${result.id}/download`
            : (result.filename || file.name);

          setFormState(prevState => ({
            ...prevState,
            image: assetPath
          }));

          // Refresh assets list to include the new upload
          searchAssets(assetSearchTerm);

          alert('Image uploaded successfully!');
        } else {
          const errorData = await response.json();
          console.error('Upload failed:', errorData);
          alert(`Upload failed: ${errorData.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleImageSelect = (e) => {
    setFormState(prevState => ({ ...prevState, image: e.target.value }));
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    // Set loading state during deletion
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Bearer ' + getToken()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      // Optimistically update the UI by removing the product from the current list
      setProducts(prevProducts => {
        const filteredProducts = prevProducts.filter(product => product.id !== productId);
        console.log('AdminProducts: Optimistically updated products count:', filteredProducts.length);
        return filteredProducts;
      });

      setLoading(false);
    } catch (error) {
      console.error('AdminProducts: Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/export/csv`, {
        headers: {
          'Authorization': 'Bearer ' + getToken()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_catalog.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleImportConfirm = () => {
    importFileInputRef.current.click();
    setShowImportModal(false);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvData = event.target.result;
        const response = await fetch(`${API_URL}/import/csv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + getToken()
          },
          body: JSON.stringify({
            csvData: csvData,
            mode: importMode
          })
        });

        const result = await response.json();

        if (response.ok) {
          alert(`CSV import completed successfully! Imported ${result.imported} products.`);
          // Refresh products after upload with cache busting
          const cacheBuster = new Date().getTime();
          fetch(`${ADMIN_PRODUCTS_LIST_URL}?t=${cacheBuster}`, {
            headers: {
              'Authorization': 'Bearer ' + getToken(),
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          })
            .then(res => res.json())
            .then(setProducts);
        } else {
          alert(`CSV import failed: ${result.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error uploading CSV:', error);
        alert('Failed to upload CSV. Please try again.');
      }
    };

    reader.readAsText(file);
  };

  // Export single product as ZIP
  const handleExportProduct = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/${productId}/export`, {
        headers: {
          'Authorization': 'Bearer ' + getToken()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export product');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const filename = parseContentDispositionFilename(
        response.headers.get('Content-Disposition'),
        'product.zip',
      );

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting product:', error);
      alert('Failed to export product. Please try again.');
    }
  };

  // Handle ZIP import click
  const handleZIPImportClick = () => {
    setShowZIPImportModal(true);
  };

  // Handle ZIP import confirm
  const handleZIPImportConfirm = () => {
    zipImportFileInputRef.current.click();
    setShowZIPImportModal(false);
  };

  // Handle ZIP file upload
  const handleZIPUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Please select a ZIP file.');
      e.target.value = '';
      return;
    }

    // Store file for potential retry
    pendingImportFile.current = file;

    // Show progress modal
    setImportProgress({
      isLoading: true,
      step: 'uploading',
      message: 'Uploading ZIP file...',
      error: null,
      productData: null,
      existingProduct: null
    });

    await processZIPImport(file);
  };

  // Process ZIP import with conflict resolution
  const processZIPImport = async (file, conflictMode = null) => {
    const formData = new FormData();
    formData.append('zipfile', file);
    if (conflictMode) {
      formData.append('conflictMode', conflictMode);
    }

    try {
      setImportProgress(prev => ({ ...prev, step: 'uploading', message: 'Uploading ZIP file to server...' }));

      const response = await fetch(`${API_URL}/import/zip`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + getToken()
        },
        body: formData
      });

      // Show processing step while parsing response
      setImportProgress(prev => ({ ...prev, step: 'processing', message: 'Processing ZIP file contents...' }));

      // Parse response JSON (works even for 409 status)
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, treat as error
        setImportProgress({
          isLoading: false,
          step: 'error',
          message: '',
          error: 'Failed to parse server response. Please try again.',
          productData: null,
          existingProduct: null
        });
        return;
      }

      // Handle conflict - show conflict resolution modal
      if (response.status === 409 && result.conflict && result.requiresConflictResolution) {
        setImportProgress({
          isLoading: false,
          step: 'conflict',
          message: 'Product with same SKU exists',
          error: null,
          productData: result.newProduct,
          existingProduct: result.existingProduct
        });
        setConflictResolution('update'); // Default to update
        setShowConflictModal(true);
        return;
      }

      if (response.ok) {
        // Show finalizing step
        setImportProgress(prev => ({ ...prev, step: 'finalizing', message: 'Finalizing import...' }));

        // Refresh products after successful import
        const cacheBuster = new Date().getTime();
        fetch(`${ADMIN_PRODUCTS_LIST_URL}?t=${cacheBuster}`, {
          headers: {
            'Authorization': 'Bearer ' + getToken(),
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        })
          .then(res => res.json())
          .then(setProducts)
          .then(() => {
            // Show success (single product or bulk ZIP from multi-select export)
            setImportProgress({
              isLoading: false,
              step: 'success',
              message: result.bulk
                ? (result.message || `Imported ${result.imported ?? result.results?.length ?? 0} products from ZIP.`)
                : (result.message || 'Product imported successfully!'),
              error: null,
              productData: result.bulk ? null : (result.product || null),
              existingProduct: null
            });
          });
      } else {
        setImportProgress({
          isLoading: false,
          step: 'error',
          message: '',
          error: result.error || 'Unknown error occurred',
          productData: null,
          existingProduct: null
        });
      }
    } catch (error) {
      console.error('Error uploading ZIP:', error);
      setImportProgress({
        isLoading: false,
        step: 'error',
        message: '',
        error: 'Failed to upload ZIP file. Please try again.',
        productData: null,
        existingProduct: null
      });
    }
  };

  // Handle conflict resolution
  const handleConflictResolution = async (resolution) => {
    setShowConflictModal(false);
    setConflictResolution(resolution);

    // Re-upload with conflict mode using stored file
    if (pendingImportFile.current) {
      setImportProgress({
        isLoading: true,
        step: resolution === 'update' ? 'updating' : 'creating',
        message: resolution === 'update'
          ? 'Updating existing product...'
          : 'Creating new product with different SKU...',
        error: null,
        productData: null,
        existingProduct: null
      });
      await processZIPImport(pendingImportFile.current, resolution);
    }
  };

  // Export selected products as ZIP
  const handleExportSelected = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to export.');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/export/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + getToken()
        },
        body: JSON.stringify({ productIds: selectedProducts })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export products');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const filename = parseContentDispositionFilename(
        response.headers.get('Content-Disposition'),
        'products_export.zip',
      );

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setLoading(false);
      alert(`Successfully exported ${selectedProducts.length} product(s) as ZIP file!`);
    } catch (error) {
      console.error('Error exporting selected products:', error);
      setLoading(false);
      alert('Failed to export products. Please try again.');
    }
  };


  if (loading) return <LoadingSpinner size="medium" message="Loading products..." />;

  return (
    <div className="admin-products-container full-width">
      <div className="page-header">
        <div className="header-left">
          <h2>Manage Products</h2>
          <span className="product-count-badge">
            {filteredProducts.length} {filteredProducts.length !== products.length && `of ${products.length}`} Products
          </span>
        </div>
        <div className="header-actions">
          <button
            onClick={downloadCSV}
            className="add-product-button secondary"
            title="Download all products as CSV file"
          >
            {getIcon('download')} Export All Products
          </button>
          <button
            onClick={handleImportClick}
            className="add-product-button secondary"
            title="Import products from CSV file"
          >
            {getIcon('upload')} Import Products (CSV)
          </button>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleZIPImportClick}
              className="add-product-button secondary"
              title="Import product from ZIP file"
            >
              {getIcon('upload')} Import Product (ZIP)
            </button>
            <HelpPopup title="Import Product from ZIP" position="bottom">
              <p><strong>Import a product</strong> from a ZIP file previously exported from this system.</p>

              <p><strong>What's included in the ZIP:</strong></p>
              <ul>
                <li><code>product.json</code> - Product metadata (name, price, description, SKU, inventory, etc.)</li>
                <li>Product image file (if present)</li>
                <li><code>README.txt</code> - Import instructions</li>
              </ul>

              <div className="tip">
                💡 <strong>Smart Conflict Handling:</strong> If a product with the same SKU exists, you'll be prompted to update, create with a new SKU, or skip.
              </div>

              <p><strong>After import:</strong> The product and its image will be automatically created or updated in the system.</p>
            </HelpPopup>
          </div>
          <input
            ref={importFileInputRef}
            type="file"
            accept=".csv"
            onChange={handleCSVUpload}
            style={{ display: 'none' }}
          />
          <input
            ref={zipImportFileInputRef}
            type="file"
            accept=".zip"
            onChange={handleZIPUpload}
            style={{ display: 'none' }}
          />
          <button onClick={handleAddProduct} className="add-product-button">
            {getIcon('add')} Add New Product
          </button>
        </div>
      </div>

      {/* Search and Multi-select Controls */}
      <div className="products-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="🔍 Search products by name, SKU, author, artist, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="product-search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="clear-search-button"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="selection-controls">
          <button
            onClick={handleSelectAll}
            className="select-all-button"
            title={selectedProducts.length === filteredProducts.length ? 'Deselect all' : 'Select all visible products'}
          >
            {selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 ? '☑' : '☐'} {selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 ? 'Deselect All' : 'Select All'}
          </button>

          {selectedProducts.length > 0 && (
            <>
              <span className="selected-count">
                {selectedProducts.length} selected
              </span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={handleExportSelected}
                  className="export-selected-button"
                  title="Export selected products as ZIP file"
                >
                  {getIcon('download')} Export Selected
                </button>
                <HelpPopup title="Export Products as ZIP" position="bottom">
                  <p><strong>Export selected products</strong> to create a backup or transfer them to another environment.</p>

                  <p><strong>What's included in the ZIP:</strong></p>
                  <ul>
                    <li><code>product.json</code> files for each product</li>
                    <li>All product images</li>
                    <li>Complete product metadata (SKU, pricing, inventory, descriptions)</li>
                  </ul>

                  <div className="tip">
                    💡 <strong>Use Case:</strong> Perfect for migrating products between dev and production, or creating backups before major changes.
                  </div>

                  <p><strong>To import later:</strong> Use the "Import Product (ZIP)" button with the exported file.</p>
                </HelpPopup>
              </div>
              <button
                onClick={handleDuplicateSelected}
                className="duplicate-button"
                title="Duplicate selected products"
              >
                {getIcon('copy')} Duplicate Selected
              </button>
              <button
                onClick={handleDeleteSelected}
                className="delete-selected-button"
                title="Delete selected products"
              >
                {getIcon('delete')} Delete Selected
              </button>
              <button
                onClick={handleClearSelection}
                className="clear-selection-button"
                title="Clear selection"
              >
                Clear
              </button>
            </>
          )}
        </div>
      </div>
      {/* Full Screen Edit Modal */}
      {isEditing && (
        <div className="edit-modal-overlay" onClick={() => { if (!isSavingProduct) { setProductSaveError(null); setIsEditing(false); } }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h2>✏️ Edit Product: {formState.name}</h2>
              <div className="edit-modal-header-actions">
                <button
                  type="button"
                  className="save-button save-button--header"
                  disabled={isSavingProduct}
                  onClick={() => {
                    // Trigger form submission
                    const form = document.querySelector('.edit-form');
                    if (form) {
                      form.requestSubmit();
                    }
                  }}
                  title="Save changes"
                >
                  {isSavingProduct ? '⏳ Saving...' : '💾 Save Changes'}
                </button>
                <button
                  className="close-button"
                  onClick={() => { if (!isSavingProduct) { setProductSaveError(null); setIsEditing(false); } }}
                  title="Close editor"
                  disabled={isSavingProduct}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="edit-modal-content">
              {/* Left Panel - Product Preview & Image */}
              <div className="edit-left-panel">
                <div className="product-preview-card">
                  <h3>📸 Product Preview</h3>
                  <div className="preview-image-container">
                    <AsyncProductImage
                      key={`preview-${formState.image || 'default'}`}
                      product={formState}
                      className="preview-main-image"
                      alt="Product preview"
                      onError={(e) => {
                        console.log('Product preview image failed to load, using placeholder');
                      }}
                    />
                  </div>
                  <div className="preview-details">
                    <div className="preview-title">{formState.name || 'Product Name'}</div>
                    <div className="preview-category">
                      <span className="category-icon">{getCategoryIcon(formState)}</span>
                      {formState.type || 'Category'}
                    </div>
                    <div className="preview-price">${formState.price || '0.00'}</div>
                  </div>
                </div>

                <div className="current-image-info">
                  <h4>🖼️ Current Image</h4>
                  <div className="image-path">
                    {formState.image ? (
                      <span className="image-path-text">{formState.image}</span>
                    ) : (
                      <span className="no-image">No image selected</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel - Edit Form */}
              <div className="edit-right-panel">
                <form onSubmit={handleFormSubmit} className="edit-form">
                  {productSaveError && (
                    <div className="admin-products-save-error" role="alert">
                      <div className="admin-products-save-error__body">
                        <strong className="admin-products-save-error__summary">{productSaveError.summary}</strong>
                        {productSaveError.hint ? (
                          <p className="admin-products-save-error__hint">{productSaveError.hint}</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="admin-products-save-error__dismiss"
                        onClick={() => setProductSaveError(null)}
                        aria-label="Dismiss error"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {/* Form Tabs */}
                  <div className="form-tabs">
                    <button type="button" className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => handleTabClick('basic')}>
                      {getIcon('edit')} Basic Info
                    </button>
                    <button type="button" className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`} onClick={() => handleTabClick('pricing')}>
                      {getIcon('price')} Pricing
                    </button>
                    <button type="button" className={`tab-button ${activeTab === 'details' ? 'active' : ''}`} onClick={() => handleTabClick('details')}>
                      📄 Details
                    </button>
                    {formState.is_bundle && (
                      <button type="button" className={`tab-button ${activeTab === 'bundle' ? 'active' : ''}`} onClick={() => handleTabClick('bundle')}>
                        📦 Bundle Config
                      </button>
                    )}
                    <button type="button" className={`tab-button ${activeTab === 'variants' ? 'active' : ''}`} onClick={() => handleTabClick('variants')}>
                      🎨 Variants
                    </button>
                    <button type="button" className={`tab-button ${activeTab === 'image' ? 'active' : ''}`} onClick={() => handleTabClick('image')}>
                      🖼️ Image
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="tab-content">
                    {/* Basic Information Tab */}
                    <div className={`tab-panel ${activeTab === 'basic' ? 'active' : ''}`} id="basic">
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label className="form-label">Product Name *</label>
                          <input
                            name="name"
                            value={formState.name}
                            onChange={handleInputChange}
                            placeholder="Enter product name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            SKU
                            <HelpPopup title="Understanding SKU" position="right">
                              <p><strong>SKU</strong> (Stock Keeping Unit) is a unique identifier for each product.</p>

                              <div className="example-box">
                                <strong>Example SKU:</strong><br />
                                CB-FID-001 (Cape Breton Fiddle Book #001)
                              </div>

                              <p><strong>Best Practices:</strong></p>
                              <ul>
                                <li>Keep it short and memorable</li>
                                <li>Use consistent format across products</li>
                                <li>Include category or product type prefix</li>
                              </ul>

                              <div className="tip">
                                💡 Click <strong>Generate</strong> to auto-create a unique SKU based on the product name
                              </div>
                            </HelpPopup>
                          </label>
                          <div className="input-with-button">
                            <input
                              name="sku"
                              value={formState.sku}
                              onChange={handleInputChange}
                              placeholder="Auto-generated"
                              className="form-input"
                            />
                            <button
                              type="button"
                              onClick={() => setFormState(prev => ({ ...prev, sku: generateProductNumber() }))}
                              className="generate-button"
                            >
                              Generate
                            </button>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Category *
                            <HelpPopup title="Product Categories" position="right">
                              <p><strong>Categories</strong> organize your products and help customers find what they're looking for.</p>

                              <p><strong>Common Categories:</strong></p>
                              <ul>
                                <li><strong>📚 Book:</strong> Physical or instructional books</li>
                                <li><strong>💿 CD:</strong> Music recordings and albums</li>
                                <li><strong>🎵 Sheet Music:</strong> Printed music notation</li>
                                <li><strong>📱 Digital:</strong> Downloadable content</li>
                                <li><strong>🎁 Bundle:</strong> Product packages</li>
                              </ul>

                              <div className="tip">
                                💡 Click <strong>+ Create Category</strong> to add new categories in a separate tab
                              </div>

                              <p>Categories affect product display, filtering, and related product suggestions.</p>
                            </HelpPopup>
                          </label>
                          <div className="category-selector-group">
                            <select
                              key={`category-select-edit-${categoriesToUse.length}-${categoriesToUse[0]?.name || 'empty'}`}
                              name="type"
                              value={formState.type || 'Book'}
                              onChange={handleInputChange}
                              className="form-select"
                            >
                              {console.log('🎨 ========== RENDERING EDIT DROPDOWN ==========') || null}
                              {console.log('🎨 categoriesToUse.length:', categoriesToUse.length) || null}
                              {console.log('🎨 categoriesToUse:', categoriesToUse.map(c => `${c.icon} ${c.name}`)) || null}
                              {console.log('🎨 formState.type:', formState.type) || null}

                              {categoriesToUse.map(cat => (
                                <option key={cat.id} value={cat.name}>
                                  {cat.icon} {cat.name}
                                </option>
                              ))}
                              {/* Add current value if it doesn't exist in categories */}
                              {formState.type && !categoriesToUse.find(cat => cat.name === formState.type) && (
                                <option value={formState.type}>
                                  {formState.type} (Current)
                                </option>
                              )}
                            </select>
                            <a
                              href="/admin/categories"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="manage-categories-link"
                              title="Manage categories in new tab"
                              onClick={(e) => {
                                // Refresh categories when user clicks to manage categories
                                setTimeout(() => fetchCategories(), 1000);
                              }}
                            >
                              + Create Category
                            </a>
                          </div>
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label virtual-product-label">
                            <input
                              type="checkbox"
                              name="shipping_enabled"
                              checked={!formState.shipping_enabled}
                              onChange={(e) => setFormState(prev => ({ ...prev, shipping_enabled: !e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span className="checkbox-text">
                              📱 Virtual/Digital Product (No shipping required)
                            </span>
                          </label>
                          <p className="field-help-text">
                            Check this if the product is digital (e.g., PDF, digital download) and doesn't require physical shipping
                          </p>
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label virtual-product-label">
                            <input
                              type="checkbox"
                              checked={!!formState.is_digital_album}
                              onChange={(e) => handleDigitalAlbumToggle(e.target.checked)}
                              className="form-checkbox"
                            />
                            <span className="checkbox-text">
                              📥 Digital album download (licensed ZIP after purchase)
                            </span>
                          </label>
                          <p className="field-help-text">
                            Upload a master <strong>.zip</strong> of MP3s (and optional cover image/PDF inside the ZIP).
                            The file is stored privately — not in the public shop. Customers get a licensed copy with their name after purchase.
                          </p>
                        </div>

                        {formState.is_digital_album && (
                          <div className="form-group full-width">
                            <label className="form-label">Master album ZIP *</label>
                            <input
                              ref={digitalZipInputRef}
                              type="file"
                              accept=".zip,application/zip,application/x-zip-compressed"
                              style={{ display: 'none' }}
                              onChange={handleDigitalZipUpload}
                            />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={digitalZipUploading}
                                onClick={() => digitalZipInputRef.current?.click()}
                              >
                                {digitalZipUploading ? 'Uploading…' : formState.digital_bundle_asset_id ? 'Replace ZIP' : 'Upload album ZIP'}
                              </button>
                              {formState.digital_bundle_asset_id && (
                                <span style={{ fontSize: '0.9rem' }}>
                                  ✓ Asset #{formState.digital_bundle_asset_id}
                                  {formState.digital_bundle_asset_name ? ` — ${formState.digital_bundle_asset_name}` : ''}
                                </span>
                              )}
                            </div>
                            {digitalZipUploadMessage && (
                              <p
                                className="field-help-text"
                                style={{ color: digitalZipUploadMessage.type === 'error' ? '#b91c1c' : '#15803d' }}
                              >
                                {digitalZipUploadMessage.text}
                                {digitalZipUploadMessage.tracks?.length > 0 && (
                                  <>
                                    <br />
                                    Tracks: {digitalZipUploadMessage.tracks.join(', ')}
                                  </>
                                )}
                              </p>
                            )}
                            <p className="field-help-text">
                              Upload any layout — we adjust the ZIP after upload: remove Mac junk, flatten one folder level,
                              sort tracks into <code>tracks/</code>, and place cover at root if present.
                            </p>
                          </div>
                        )}

                        {formState.is_digital_album && (
                          <details className="form-group full-width" style={{ marginBottom: '0.75rem' }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Advanced: legacy server folder (optional)</summary>
                            <div style={{ marginTop: '0.5rem' }}>
                              <label className="form-label">Bundle slug</label>
                              <input
                                type="text"
                                name="digital_bundle_slug"
                                value={formState.digital_bundle_slug || ''}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="heather-hill-digital"
                              />
                              <p className="field-help-text">
                                Only if files live under <code>backend/private/digital/&lt;slug&gt;/</code> instead of an uploaded ZIP.
                              </p>
                            </div>
                          </details>
                        )}

                        {!formState.is_digital_album && (
                          <div className="form-group full-width">
                            <label className="form-label">Digital alternative (when physical CD is out of stock)</label>
                            <select
                              name="related_digital_product_id"
                              value={formState.related_digital_product_id || ''}
                              onChange={handleInputChange}
                              className="form-input"
                            >
                              <option value="">None — no digital fallback</option>
                              {digitalAlbumOptions.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} (${((p.price_cents || 0) / 100).toFixed(2)})
                                </option>
                              ))}
                            </select>
                            <p className="field-help-text">
                              Shown on the product page when inventory is 0 so customers can buy the digital version instead.
                            </p>
                          </div>
                        )}

                        {formState.shipping_enabled && !formState.is_digital_album && (
                          <>
                            <div className="form-group">
                              <label className="form-label">Minimum Shipping Cost - Canada (CAD)</label>
                              <div className="price-input-container">
                                <span className="currency-symbol">$</span>
                                <input
                                  type="number"
                                  name="minimum_shipping"
                                  value={formState.minimum_shipping}
                                  onChange={handleInputChange}
                                  className="form-input price-input"
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                              <p className="field-help-text">
                                Minimum shipping cost for <strong>Canadian domestic orders</strong>. At checkout, the highest minimum from all cart items will be used.
                              </p>
                            </div>

                            <div className="form-group">
                              <label className="form-label">Minimum Shipping Cost - International (CAD)</label>
                              <div className="price-input-container">
                                <span className="currency-symbol">$</span>
                                <input
                                  type="number"
                                  name="minimum_shipping_international"
                                  value={formState.minimum_shipping_international}
                                  onChange={handleInputChange}
                                  className="form-input price-input"
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                              <p className="field-help-text">
                                Minimum shipping cost for <strong>international orders</strong> (outside Canada). At checkout, the highest minimum from all cart items will be used.
                              </p>
                            </div>

                            <div className="form-group">
                              <label className="form-label">Weight (kg)</label>
                              <div className="price-input-container">
                                <input
                                  type="number"
                                  name="weight_kg"
                                  value={formState.weight_kg}
                                  onChange={handleInputChange}
                                  className="form-input price-input"
                                  placeholder="0.5"
                                  step="0.01"
                                  min="0"
                                />
                                <span style={{ marginLeft: '8px' }}>kg</span>
                              </div>
                              <p className="field-help-text">
                                {formState.is_bundle ? (
                                  <>
                                    Not used for packages — shipping weight is summed from the products the
                                    customer selects, so set the weight on each of those instead.
                                  </>
                                ) : (
                                  <>
                                    Product weight in kilograms. Used for calculating weight-based shipping charges
                                    and the estimated weight shown at checkout. Falls back to 0.2 kg if left blank.
                                  </>
                                )}
                              </p>
                            </div>
                          </>
                        )}

                        <div className="form-group full-width">
                          <label className="form-label virtual-product-label">
                            <input
                              type="checkbox"
                              name="is_bundle"
                              checked={formState.is_bundle}
                              onChange={(e) => {
                                setFormState(prev => ({ ...prev, is_bundle: e.target.checked }));
                                if (e.target.checked && activeTab !== 'bundle') {
                                  setActiveTab('bundle');
                                }
                              }}
                              className="form-checkbox"
                            />
                            <span className="checkbox-text">
                              📦 Bundle Product (Customers can select from predefined products)
                            </span>
                          </label>
                          <p className="field-help-text">
                            Check this to create a bundle product where customers can select multiple items from a predefined list with volume-based discounts
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Pricing Tab */}
                    <div className={`tab-panel ${activeTab === 'pricing' ? 'active' : ''}`} id="pricing">
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Price (CAD) *</label>
                          <div className="price-input-container">
                            <input
                              name="price"
                              type="number"
                              value={formState.price}
                              onChange={handleInputChange}
                              placeholder="0 for free"
                              className="form-input price-input"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Inventory *</label>
                          <input
                            name="inventory"
                            type="number"
                            value={formState.inventory}
                            onChange={handleInputChange}
                            placeholder="0"
                            className="form-input"
                            min="0"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              name="featured"
                              type="checkbox"
                              checked={formState.featured || false}
                              onChange={(e) => setFormState(prev => ({ ...prev, featured: e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span>⭐ Featured Product (shows first on shop page)</span>
                          </label>
                        </div>

                        <div className="form-group">
                          <label className="form-label checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              name="hide_from_shop"
                              type="checkbox"
                              checked={formState.hide_from_shop || false}
                              onChange={(e) => setFormState(prev => ({ ...prev, hide_from_shop: e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span>🙈 Hide from shop page (direct product link still works)</span>
                          </label>
                          <p className="field-help-text" style={{ marginTop: '4px', marginLeft: '28px' }}>
                            Product stays in admin and can be sold via /product/ID or checkout promos; it will not appear on /shop.
                          </p>
                        </div>

                        <div className="form-group">
                          <label className="form-label checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              name="checkout_promo"
                              type="checkbox"
                              checked={formState.checkout_promo || false}
                              onChange={(e) => setFormState(prev => ({ ...prev, checkout_promo: e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span>🎁 Add free at checkout (promotional gift when cart has physical items; skipped for digital-only carts)</span>
                          </label>
                          <p className="field-help-text" style={{ marginTop: '4px', marginLeft: '28px' }}>
                            Line price is $0. Skipped if the customer already has this product in the cart. Not used for bundles or variant products.
                          </p>
                        </div>
                      </div>

                      <div className="wholesale-section">
                        <h4>🏢 Wholesale Pricing</h4>
                        <div className="wholesale-grid">
                          {['standard', 'premium', 'volume'].map(tier => (
                            <div key={tier} className="wholesale-tier">
                              <div className="tier-header">
                                <span className="tier-name">{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                                <span className="tier-badge">{tier === 'standard' ? '🥉' : tier === 'premium' ? '🥈' : '🥇'}</span>
                              </div>
                              <div className="tier-inputs">
                                <div className="tier-input-group">
                                  <label>Min Qty:</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={formState.wholesale_pricing[tier].min_qty}
                                    onChange={e => handleWholesaleChange(tier, 'min_qty', parseInt(e.target.value))}
                                    className="tier-input"
                                  />
                                </div>
                                <div className="tier-input-group">
                                  <label>Discount %:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formState.wholesale_pricing[tier].discount}
                                    onChange={e => handleWholesaleChange(tier, 'discount', parseInt(e.target.value))}
                                    className="tier-input"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Tax Configuration Section */}
                      <div className="tax-config-section">
                        <h4 className="tax-config-section__title">
                          💰 Tax Configuration
                          <span className="tax-config-section__subtitle">
                            (Canadian GST/HST Compliance)
                          </span>
                        </h4>

                        <div className="form-group">
                          <label className="form-label">Tax Type</label>
                          <select
                            name="tax_type"
                            value={formState.tax_type || 'auto'}
                            onChange={handleInputChange}
                            className="form-select"
                            style={{ maxWidth: '400px' }}
                          >
                            <option value="auto">🤖 Auto (CRA Rules) - Books get 5% GST, others use province rate</option>
                            <option value="gst">📚 GST Only (5%) - Point-of-Sale Rebate for Books</option>
                            <option value="hst">🏢 Full HST - Use Provincial Rate (13-15%)</option>
                            <option value="custom">⚙️ Custom Rate - Override with specific percentage</option>
                            <option value="exempt">🆓 Tax Exempt - No tax charged (e.g., educational)</option>
                          </select>
                          <p className="field-help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                            {formState.tax_type === 'auto' && '✓ Recommended: Automatically applies CRA point-of-sale rebate for books'}
                            {formState.tax_type === 'gst' && '📚 Forces 5% GST for this product regardless of category'}
                            {formState.tax_type === 'hst' && '⚠️ Charges full provincial HST rate (use for non-book items)'}
                            {formState.tax_type === 'custom' && '⚙️ Set your own tax rate below (use with caution)'}
                            {formState.tax_type === 'exempt' && '⚠️ No tax will be charged (ensure CRA compliance)'}
                          </p>
                        </div>

                        {formState.tax_type === 'custom' && (
                          <div className="form-group">
                            <label className="form-label">Custom Tax Rate (%)</label>
                            <div className="price-input-container" style={{ maxWidth: '200px' }}>
                              <input
                                type="number"
                                name="tax_rate"
                                value={formState.tax_rate || ''}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="e.g., 5, 13, 15"
                                step="0.01"
                                min="0"
                                max="100"
                              />
                              <span style={{ marginLeft: '0.5rem' }}>%</span>
                            </div>
                            <p className="field-help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-error)' }}>
                              ⚠️ Custom rates override CRA rules. Ensure compliance before using.
                            </p>
                          </div>
                        )}

                        <div className="form-group">
                          <label className="form-label">Tax Configuration Notes (Internal)</label>
                          <textarea
                            name="tax_notes"
                            value={formState.tax_notes || ''}
                            onChange={handleInputChange}
                            className="form-textarea"
                            rows="2"
                            placeholder="e.g., 'Special exemption approved by CRA' or 'Educational material per policy #123'"
                            style={{ fontSize: '0.9rem' }}
                          />
                          <p className="field-help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                            Optional: Document why this tax configuration was chosen (for audit purposes)
                          </p>
                        </div>

                        {/* Tax Preview */}
                        <div className="tax-preview-panel">
                          <h5>🔍 Tax Preview (Ontario Example):</h5>
                          <div className="tax-preview-panel__body">
                            {formState.tax_type === 'auto' && (
                              formState.type && (formState.type.toLowerCase().includes('book') || formState.type.toLowerCase().includes('publication')) ? (
                                <p className="tax-preview-panel__success">
                                  ✓ <strong>Books: 5% GST</strong> (Point-of-Sale Rebate Applied)<br />
                                  <span className="tax-preview-panel__sub">
                                    Customer saves 8% vs full 13% HST in Ontario
                                  </span>
                                </p>
                              ) : (
                                <p className="tax-preview-panel__info">
                                  <strong>Non-Book: 13% HST</strong> (Full provincial rate in Ontario)
                                </p>
                              )
                            )}
                            {formState.tax_type === 'gst' && (
                              <p className="tax-preview-panel__success">
                                ✓ <strong>5% GST Only</strong> (Provincial portion rebated)
                              </p>
                            )}
                            {formState.tax_type === 'hst' && (
                              <p className="tax-preview-panel__info">
                                <strong>13% HST</strong> (Full rate - 5% GST + 8% PST in Ontario)
                              </p>
                            )}
                            {formState.tax_type === 'custom' && formState.tax_rate && (
                              <p className="tax-preview-panel__warning">
                                ⚙️ <strong>{formState.tax_rate}% Custom Rate</strong>
                              </p>
                            )}
                            {formState.tax_type === 'exempt' && (
                              <p className="tax-preview-panel__muted">
                                🆓 <strong>Tax Exempt</strong> (0% - No tax charged)
                              </p>
                            )}
                          </div>
                        </div>

                        {/* CRA Reference Link */}
                        <div className="cra-reference-panel">
                          <strong>📚 CRA Reference:</strong> <a
                            href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses/charge-collect-specific-situations/suppliers-publications.html"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            GST/HST for Suppliers of Publications
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Details Tab */}
                    <div className={`tab-panel ${activeTab === 'details' ? 'active' : ''}`} id="details">
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          name="description"
                          value={formState.description}
                          onChange={handleInputChange}
                          placeholder="Enter detailed product description..."
                          className="form-textarea"
                          rows="6"
                        />
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Author</label>
                          <input
                            name="author"
                            value={formState.author}
                            onChange={handleInputChange}
                            placeholder="Author name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Artist</label>
                          <input
                            name="artist"
                            value={formState.artist}
                            onChange={handleInputChange}
                            placeholder="Artist name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label">Source URL</label>
                          <input
                            name="source_url"
                            value={formState.source_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://www.cranfordpub.com/..."
                            className="form-input"
                            type="url"
                          />
                          <p className="field-help-text">
                            Original URL from cranfordpub.com where this product was imported from
                          </p>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Sample tunes on product page</label>
                        <input
                          name="sample_tunes_limit"
                          type="number"
                          min={1}
                          max={99}
                          value={formState.sample_tunes_limit ?? 9}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                        <p className="field-help-text">
                          Maximum number of &ldquo;Sample Tunes of this Product&rdquo; cards shown (default 9). Use the book index link for the full list.
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Tune index page</label>
                        <div className="input-with-button">
                          <input
                            name="index_page_id"
                            value={formState.index_page_id || ''}
                            onChange={handleInputChange}
                            placeholder="e.g. stubbert3rd-index or https://…/page/stubbert3rd-index"
                            className="form-input"
                            autoComplete="off"
                            style={{ flex: 1, minWidth: 0 }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowTuneIndexPicker(true)}
                            className="generate-button"
                            title="Search book-index and cd-index pages"
                          >
                            Browse…
                          </button>
                        </div>
                        <p className="field-help-text">
                          <code>content_pages.page_id</code> for the book or CD tune index (page type book-index or cd-index).
                          Leave blank to use automatic guesses from SKU and title. Paste a site path or full URL to override, or use Browse to pick a page.
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">🏷️ Tags</label>
                        <div className="input-with-button">
                          <input
                            name="tags"
                            value={formState.tags || ''}
                            onChange={handleInputChange}
                            placeholder="e.g., irish, fiddle, beginner, cape-breton (comma-separated)"
                            className="form-input"
                          />
                          <button
                            type="button"
                            onClick={generateTags}
                            className="generate-button"
                            title="Auto-generate tags from product title, description, category, author, and artist"
                          >
                            ✨ Auto-Populate
                          </button>
                        </div>
                        <p className="field-help-text">
                          Add tags to categorize and relate products. Separate multiple tags with commas. Click "Auto-Populate" to generate tags automatically.
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          🔗 Related Products
                          <HelpPopup title="Understanding Product Relationships" position="right">
                            <p><strong>Product relationships help customers discover related items!</strong></p>

                            <div className="tip">
                              💡 <strong>How it works:</strong>
                              <ul>
                                <li>Search and select specific products to recommend</li>
                                <li>Selected products appear first on the product page</li>
                                <li>Optionally include tag-based suggestions</li>
                              </ul>
                            </div>

                            <p><strong>Two Ways to Create Relationships:</strong></p>
                            <ul>
                              <li><strong>Manual Selection:</strong> Use the search box below to pick specific products</li>
                              <li><strong>Automatic Matching:</strong> Enable the checkbox to also show products with matching tags or same category</li>
                            </ul>

                            <div className="example-box">
                              <strong>Example:</strong><br />
                              For "The Cape Breton Fiddle Companion" you might manually select:<br />
                              • "Cape Breton Fiddle Music" (CD)<br />
                              • "Learn Cape Breton Style" (Book)<br />
                              <br />
                              Plus auto-suggestions from products tagged "cape-breton" or "fiddle"
                            </div>

                            <p><strong>Best Practices:</strong></p>
                            <ul>
                              <li>Select 2-4 highly relevant products manually</li>
                              <li>Use tags consistently across products for better auto-matching</li>
                              <li>Related products supersede tag-based suggestions</li>
                            </ul>
                          </HelpPopup>
                        </label>
                        <ProductSearch
                          selectedProductIds={formState.related_products ? formState.related_products.split(',').filter(id => id).map(id => parseInt(id.trim())) : []}
                          onSelectionChange={(productIds) => {
                            setFormState(prev => ({
                              ...prev,
                              related_products: productIds.join(',')
                            }));
                          }}
                          excludeProductId={isEditing ? parseInt(formState.id) : null}
                        />
                        <p className="field-help-text">
                          Select products that are related to this one. They will be displayed on the product detail page.
                        </p>

                        <div className="form-checkbox-group">
                          <p className="form-checkbox-group__title">
                            🎯 Related Products Display Options:
                          </p>

                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              name="related_include_explicit"
                              checked={formState.related_include_explicit !== false}
                              onChange={(e) => setFormState(prev => ({
                                ...prev,
                                related_include_explicit: e.target.checked
                              }))}
                              className="form-checkbox"
                              style={{ marginTop: '3px' }}
                            />
                            <span style={{ marginLeft: '8px' }}>
                              <strong>Show Manually Selected Products</strong><br />
                              <small style={{ color: 'var(--color-text-secondary)' }}>Display the specific products you selected above</small>
                            </span>
                          </label>

                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              name="related_include_tags"
                              checked={formState.related_include_tags !== false}
                              onChange={(e) => setFormState(prev => ({
                                ...prev,
                                related_include_tags: e.target.checked
                              }))}
                              className="form-checkbox"
                              style={{ marginTop: '3px' }}
                            />
                            <span style={{ marginLeft: '8px' }}>
                              <strong>Include Tag-Based Suggestions</strong><br />
                              <small style={{ color: 'var(--color-text-secondary)' }}>Auto-suggest products with matching tags</small>
                            </span>
                          </label>

                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <input
                              type="checkbox"
                              name="related_include_category"
                              checked={formState.related_include_category !== false}
                              onChange={(e) => setFormState(prev => ({
                                ...prev,
                                related_include_category: e.target.checked
                              }))}
                              className="form-checkbox"
                              style={{ marginTop: '3px' }}
                            />
                            <span style={{ marginLeft: '8px' }}>
                              <strong>Include Category-Based Suggestions</strong><br />
                              <small style={{ color: 'var(--color-text-secondary)' }}>Auto-suggest products from the same category</small>
                            </span>
                          </label>

                          <p className="field-help-text" style={{ marginTop: '12px', marginLeft: '0', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                            💡 <strong>Tip:</strong> Enable multiple options to combine different matching strategies. Products appear in priority: Manual → Tags → Category.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bundle Configuration Tab */}
                    {formState.is_bundle && (
                      <div className={`tab-panel ${activeTab === 'bundle' ? 'active' : ''}`} id="bundle">
                        <div className="bundle-config-intro">
                          <strong>📦 Bundle Product Configuration</strong>
                          <p>
                            Configure which products customers can select as part of this bundle, and set volume-based discount rules.
                          </p>
                        </div>

                        {/* Fixed Price Bundle Options */}
                        <div className="form-group bundle-options-panel">
                          <label className="form-label bundle-options-panel__title">
                            🏷️ Fixed Price Bundle Options
                          </label>
                          <p className="field-help-text" style={{ marginBottom: '15px' }}>
                            Define specific combinations with fixed prices (e.g., &quot;Book &amp; CD&quot; for $25). These options are shown as package buttons on the product page.
                            Optional <code>taxAllocations</code> JSON per option (via DB/script) splits Canadian book (5%) vs digital/CD (14%) tax.
                          </p>

                          {(formState.bundle_options || []).map((option, index) => (
                            <div key={index} className="bundle-option-card">
                              <div className="bundle-option-card__header">
                                <strong className="bundle-option-card__title">Option {index + 1}</strong>
                                <button type="button" onClick={() => removeBundleOption(index)} className="bundle-option-remove">
                                  {getIcon('delete')} Remove
                                </button>
                              </div>
                              <div className="form-grid">
                                <div className="form-group">
                                  <label className="form-label">Option Name</label>
                                  <input
                                    value={option.name}
                                    onChange={(e) => updateBundleOption(index, 'name', e.target.value)}
                                    placeholder="e.g., Book & CD Bundle"
                                    className="form-input"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Fixed Price ($)</label>
                                  <input
                                    type="number"
                                    value={option.price}
                                    onChange={(e) => updateBundleOption(index, 'price', e.target.value)}
                                    placeholder="24.95"
                                    className="form-input"
                                    step="0.01"
                                  />
                                </div>
                              </div>
                              <div className="form-group">
                                <label className="form-label">Included Product IDs</label>
                                <input
                                  value={option.items ? option.items.join(',') : ''}
                                  onChange={(e) => updateBundleOption(index, 'items', e.target.value.split(',').map(s => s.trim()))}
                                  placeholder="e.g., 703, 704"
                                  className="form-input"
                                />
                                <small className="field-help-text">Comma separated IDs of products included in this option.</small>
                              </div>
                            </div>
                          ))}

                          <button type="button" onClick={addBundleOption} className="admin-products-secondary-button">
                            + Add Fixed Price Option
                          </button>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Bundle Products Selection
                            <HelpPopup title="Bundle Products" position="right">
                              <p><strong>How Bundle Products Work:</strong></p>
                              <p>Select which products customers can choose from when purchasing this bundle.</p>

                              <div className="example-box">
                                <strong>Example:</strong><br />
                                Create a "Celtic Music Collection" bundle where customers can select 5+ books from your catalog of Irish, Scottish, and Cape Breton music books.
                              </div>

                              <p><strong>Pricing:</strong></p>
                              <ul>
                                <li>Set minimum quantity (e.g., 5 books)</li>
                                <li>Set discount percentage (e.g., 30% off)</li>
                                <li>Customers select products and get the discount applied</li>
                              </ul>
                            </HelpPopup>
                          </label>

                          <button
                            type="button"
                            onClick={() => setShowBundlePickerModal(true)}
                            className="bundle-picker-open-button"
                            style={{
                              width: '100%',
                              padding: '16px 24px',
                              background: 'var(--color-primary)',
                              color: 'var(--color-background)',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '10px',
                              transition: 'all 0.2s',
                              marginBottom: '15px'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = 'var(--color-primary-dark)';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = 'var(--color-primary)';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            <span style={{ fontSize: '20px' }}>🔍</span>
                            <span>
                              {formState.bundle_products && formState.bundle_products.split(',').filter(id => id).length > 0
                                ? `Edit Selected Products (${formState.bundle_products.split(',').filter(id => id).length} selected)`
                                : 'Select Products for Bundle'}
                            </span>
                          </button>

                          {formState.bundle_products && formState.bundle_products.split(',').filter(id => id).length > 0 && (() => {
                            const selectedIds = formState.bundle_products.split(',').filter(id => id);
                            const selectedProducts = selectedIds.map(id =>
                              products.find(p => p.id === parseInt(id))
                            ).filter(p => p);

                            return (
                              <div className="bundle-selected-summary">
                                <strong>{selectedIds.length} products selected</strong>
                                <div className="bundle-selected-summary__meta">
                                  {selectedProducts.length > 0 ? (
                                    <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                                      {selectedProducts.slice(0, 10).map(product => (
                                        <li key={product.id} style={{ marginBottom: '3px' }}>
                                          {product.name} <span className="bundle-selected-summary__id">(ID: {product.id})</span>
                                        </li>
                                      ))}
                                      {selectedProducts.length > 10 && (
                                        <li className="bundle-selected-summary__id" style={{ fontStyle: 'italic' }}>
                                          and {selectedProducts.length - 10} more...
                                        </li>
                                      )}
                                    </ul>
                                  ) : (
                                    <p style={{ margin: '5px 0 0 0' }}>
                                      Product IDs: {selectedIds.slice(0, 10).join(', ')}
                                      {selectedIds.length > 10 && '...'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          <p className="field-help-text">
                            Click the button above to open a searchable modal where you can select multiple products for this bundle. Customers will see these products with checkboxes on the bundle product page.
                          </p>
                        </div>

                        <div className="form-grid" style={{ marginTop: '25px' }}>
                          <div className="form-group">
                            <label className="form-label">Minimum Quantity</label>
                            <input
                              type="number"
                              name="bundle_min_quantity"
                              value={formState.bundle_min_quantity}
                              onChange={handleInputChange}
                              className="form-input"
                              placeholder="5"
                              min="1"
                              required
                            />
                            <p className="field-help-text">
                              Minimum number of products customer must select to qualify for the bundle discount
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Bundle Discount (%)</label>
                            <div className="price-input-container">
                              <input
                                type="number"
                                name="bundle_discount"
                                value={formState.bundle_discount}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="30"
                                min="0"
                                max="100"
                                step="0.01"
                                required
                              />
                              <span style={{ marginLeft: '8px' }}>%</span>
                            </div>
                            <p className="field-help-text">
                              Discount percentage applied when minimum quantity is reached (leave empty if using fixed price). Can use decimals like 23.85%
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Fixed Bundle Price ($)</label>
                            <div className="price-input-container">
                              <span style={{ marginRight: '8px' }}>$</span>
                              <input
                                type="number"
                                name="bundle_fixed_price_cents"
                                value={formState.bundle_fixed_price_cents ? (formState.bundle_fixed_price_cents / 100).toFixed(2) : ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setFormState(prev => ({
                                    ...prev,
                                    bundle_fixed_price_cents: value ? Math.round(parseFloat(value) * 100) : null
                                  }));
                                }}
                                className="form-input"
                                placeholder="24.95"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <p className="field-help-text">
                              Fixed price for the entire bundle (e.g., $24.95). If set, this overrides the percentage discount. Leave empty to use percentage discount.
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Maximum Selections</label>
                            <input
                              type="number"
                              name="bundle_max_selections"
                              value={formState.bundle_max_selections}
                              onChange={handleInputChange}
                              className="form-input"
                              placeholder="10"
                              min="1"
                              required
                            />
                            <p className="field-help-text">
                              Maximum number of products customer can select (optional limit). If same as minimum quantity, products will be auto-selected and locked.
                            </p>
                          </div>
                        </div>

                        {/* Bundle Preview */}
                        <div className="bundle-preview-panel">
                          <h4>📊 Bundle Preview</h4>
                          <div className="bundle-preview-panel__body">
                            <p style={{ margin: '5px 0' }}>
                              <strong>Products available:</strong> {formState.bundle_products ? formState.bundle_products.split(',').filter(id => id).length : 0}
                            </p>
                            <p style={{ margin: '5px 0' }}>
                              <strong>Discount rule:</strong> Select {formState.bundle_min_quantity}+ products to get {formState.bundle_discount}% off
                            </p>
                            <p style={{ margin: '5px 0' }}>
                              <strong>Selection limit:</strong> Up to {formState.bundle_max_selections} products
                            </p>
                            {formState.bundle_products && formState.bundle_products.split(',').filter(id => id).length > 0 && (
                              <div className="status-banner--success">
                                ✓ Bundle is ready! Customers can select from {formState.bundle_products.split(',').filter(id => id).length} products.
                              </div>
                            )}
                            {(!formState.bundle_products || formState.bundle_products.split(',').filter(id => id).length === 0) && (
                              <div className="status-banner--warning">
                                ⚠️ Please select at least one product for the bundle.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Variants Tab */}
                    <div className={`tab-panel ${activeTab === 'variants' ? 'active' : ''}`} id="variants">
                      <VariantConfigurator
                        productData={formState}
                        onChange={(variantData) => {
                          setFormState(prev => ({
                            ...prev,
                            ...variantData
                          }));
                        }}
                      />
                    </div>

                    {/* Image Tab */}
                    <div className={`tab-panel ${activeTab === 'image' ? 'active' : ''}`} id="image">
                      <div className="image-selection-section">
                        {/* Asset Search Section */}
                        <div className="asset-search-section">
                          <label className="form-label">
                            🔍 Search Database Assets
                            <HelpPopup title="Product Image Selection" position="right">
                              <p><strong>Three ways to add product images:</strong></p>

                              <p><strong>1. Search Database Assets (Recommended)</strong></p>
                              <ul>
                                <li>Search your uploaded assets by name or tags</li>
                                <li>Click to select any image</li>
                                <li>Images are stored centrally and can be reused</li>
                              </ul>

                              <p><strong>2. Select from Legacy Images</strong></p>
                              <ul>
                                <li>Browse images organized by category</li>
                                <li>Legacy system for backwards compatibility</li>
                              </ul>

                              <p><strong>3. Upload Custom Image</strong></p>
                              <ul>
                                <li>Upload a new image directly</li>
                                <li>Automatically added to asset database</li>
                              </ul>

                              <div className="tip">
                                💡 <strong>Best Practice:</strong> Use Database Assets for better organization and reusability!
                              </div>

                              <div className="warning">
                                ⚠️ Images should be high quality (at least 800x800px) for best display on product pages
                              </div>
                            </HelpPopup>
                          </label>
                          <div className="asset-search-container">
                            <input
                              type="text"
                              placeholder="Search for images by name, description, or tags..."
                              value={assetSearchTerm}
                              onChange={handleAssetSearchChange}
                              className="form-input asset-search-input"
                            />
                            {assetSearchLoading && <div className="search-loading">Searching...</div>}
                          </div>

                          {/* Asset Grid */}
                          <div className="asset-grid">
                            {assets.map(asset => (
                              <div
                                key={asset.id}
                                className={`asset-item ${formState.image === `/api/assets/${asset.id}/download` ? 'selected' : ''}`}
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <img
                                  src={`/api/assets/${asset.id}/thumbnail`}
                                  alt={asset.alt_text || asset.original_filename}
                                  className="asset-thumbnail"
                                />
                                <div className="asset-info">
                                  <div className="asset-name">{asset.original_filename}</div>
                                  {asset.description && (
                                    <div className="asset-description">{asset.description}</div>
                                  )}
                                  {asset.category && (
                                    <div className="asset-category">{asset.category}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                            {!assetSearchLoading && assets.length === 0 && (
                              <div className="no-assets">No assets found. Try a different search term.</div>
                            )}
                          </div>
                        </div>

                        {/* Legacy Image Selector and Upload Button Row */}
                        <div className="image-selection-row">
                          <div className="image-selector">
                            <label className="form-label">📁 Select from Legacy Images</label>
                            <select
                              name="image"
                              value={formState.image}
                              onChange={handleImageSelect}
                              className="form-select image-select"
                            >
                              <option value="">-- Choose an image --</option>
                              <optgroup label="📚 Cape Breton Books">
                                {availableImages.filter(img => img.startsWith('cape_breton/')).map(img => (
                                  <option key={img} value={img}>{img.replace('cape_breton/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🍀 Irish Books">
                                {availableImages.filter(img => img.startsWith('irish/')).map(img => (
                                  <option key={img} value={img}>{img.replace('irish/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🏴󠁧󠁢󠁳󠁣󠁴󠁿 Scottish Books">
                                {availableImages.filter(img => img.startsWith('scottish/')).map(img => (
                                  <option key={img} value={img}>{img.replace('scottish/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🍁 Canadian Books">
                                {availableImages.filter(img => img.startsWith('canadian/')).map(img => (
                                  <option key={img} value={img}>{img.replace('canadian/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🎵 Pipe Music">
                                {availableImages.filter(img => img.startsWith('pipe_music/')).map(img => (
                                  <option key={img} value={img}>{img.replace('pipe_music/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="💿 CDs">
                                {availableImages.filter(img => img.startsWith('cds/')).map(img => (
                                  <option key={img} value={img}>{img.replace('cds/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="📦 Other">
                                {availableImages.filter(img => !img.includes('/')).map(img => (
                                  <option key={img} value={img}>{img}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          <div className="image-upload-section">
                            <label htmlFor="image-upload" className="image-upload-label">
                              {uploadingImage ? '⏳ Uploading...' : '📁 Upload Custom Image'}
                            </label>
                            <input
                              ref={fileInputRef}
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              disabled={uploadingImage}
                              style={{ display: 'none' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="form-actions">
                    <button type="button" className="cancel-button" onClick={() => { setProductSaveError(null); setIsEditing(false); }} disabled={isSavingProduct}>
                      Cancel
                    </button>
                    <button type="submit" className="save-button" disabled={isSavingProduct}>
                      {isSavingProduct ? '⏳ Saving...' : '💾 Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Products Modal */}
      {showImportModal && (
        <div className="edit-modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '400px' }}>
            <div className="edit-modal-header">
              <h2>{getIcon('upload')} Import Products from CSV</h2>
              <button
                className="close-button"
                onClick={() => setShowImportModal(false)}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="edit-modal-content" style={{ padding: '30px' }}>
              <div className="import-mode-section">
                <h3 style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '600' }}>Select Import Mode</h3>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', padding: '15px', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: importMode === 'update' ? '#f0f7ff' : 'white', borderColor: importMode === 'update' ? '#4a90e2' : '#e0e0e0' }}>
                    <input
                      type="radio"
                      name="importMode"
                      value="update"
                      checked={importMode === 'update'}
                      onChange={(e) => setImportMode(e.target.value)}
                      style={{ marginRight: '12px', marginTop: '3px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>Update Mode (Recommended)</div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Add new products and update existing ones. Existing products not in the CSV will be kept.
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', padding: '15px', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: importMode === 'replace' ? '#fff7f0' : 'white', borderColor: importMode === 'replace' ? '#e2904a' : '#e0e0e0' }}>
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={(e) => setImportMode(e.target.value)}
                      style={{ marginRight: '12px', marginTop: '3px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>Replace Mode ⚠️</div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Delete ALL existing products and import only products from the CSV file.
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{ backgroundColor: '#fffbea', border: '1px solid #f7e07d', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', color: '#856404' }}>
                    <strong>💡 Tip:</strong> Export your current products first to have a backup before importing.
                  </div>
                </div>

                <button
                  onClick={handleImportConfirm}
                  className="form-submit-button"
                  style={{ width: '100%' }}
                >
                  {getIcon('upload')} Choose CSV File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Product from ZIP Modal */}
      {showZIPImportModal && (
        <div className="edit-modal-overlay" onClick={() => setShowZIPImportModal(false)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <div className="edit-modal-header">
              <h2>{getIcon('upload')} Import Product from ZIP</h2>
              <button
                className="close-button"
                onClick={() => setShowZIPImportModal(false)}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="edit-modal-content" style={{ padding: '30px' }}>
              {/* Introduction Section */}
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: 'var(--color-text-primary)'
                }}>
                  📦 ZIP File Structure
                </h3>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '15px'
                }}>
                  Upload a ZIP from a single-product export (root <code>product.json</code>) or a multi-product bulk export (<code>products/&lt;folder&gt;/product.json</code> per item). The ZIP must contain:
                </p>

                {/* Requirements List */}
                <div style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  padding: '18px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '18px', lineHeight: '1.2' }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '600',
                        marginBottom: '4px',
                        color: 'var(--color-text-primary)'
                      }}>
                        product.json
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Required - Contains all product data (name, price, description, SEO tags, meta tags, etc.)
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '18px', lineHeight: '1.2' }}>🖼️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '600',
                        marginBottom: '4px',
                        color: 'var(--color-text-primary)'
                      }}>
                        images/ folder
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Optional - Product image files will be automatically imported
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(74, 144, 226, 0.05) 100%)',
                border: '2px solid var(--color-primary)',
                borderRadius: '8px',
                padding: '18px',
                marginBottom: '25px'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--color-text-primary)',
                  lineHeight: '1.6'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: '20px', lineHeight: '1' }}>💡</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ display: 'block', marginBottom: '6px' }}>
                        How to create a ZIP file for import:
                      </strong>
                      <div style={{ fontSize: '13px', opacity: 0.9 }}>
                        Use the <strong>Export</strong> button ({getIcon('download')}) on any product card to download a complete ZIP file with all product data and images. You can then import this ZIP file here to create or update the product.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Import Behavior Info */}
              <div style={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '25px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px'
                }}>
                  <span style={{ fontSize: '18px' }}>ℹ️</span>
                  <strong style={{
                    fontSize: '14px',
                    color: 'var(--color-text-primary)'
                  }}>
                    Import Behavior
                  </strong>
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'var(--color-text-secondary)',
                  lineHeight: '1.6',
                  paddingLeft: '28px'
                }}>
                  • If a product with the same SKU exists, it will be <strong>updated</strong> with the imported data<br />
                  • If the SKU is new, a <strong>new product</strong> will be created<br />
                  • All product fields (metadata, SEO tags, images) will be imported
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={handleZIPImportConfirm}
                className="form-submit-button"
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  background: 'var(--color-success)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px var(--color-shadow))'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'var(--color-success-hover)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px var(--color-shadow))';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'var(--color-success)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px var(--color-shadow))';
                }}
              >
                {getIcon('upload')} Choose ZIP File to Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {isAdding && (
        <div className="edit-modal-overlay" onClick={() => { if (!isSavingProduct) { setProductSaveError(null); setIsAdding(false); } }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h2>➕ Add New Product</h2>
              <button
                className="close-button"
                onClick={() => { if (!isSavingProduct) { setProductSaveError(null); setIsAdding(false); } }}
                title="Close editor"
                disabled={isSavingProduct}
              >
                ✕
              </button>
            </div>
            <p className="admin-products-quick-hint">
              To save: enter a <strong>name</strong> under Basic Info, then a <strong>price</strong> and <strong>stock quantity</strong> under Pricing.
              You need an admin session with permission to edit products. If save fails, read the red message below—often it is an expired login, API offline, or duplicate SKU.
            </p>

            <div className="edit-modal-content">
              {/* Left Panel - Product Preview & Image */}
              <div className="edit-left-panel">
                <div className="product-preview-card">
                  <h3>📸 Product Preview</h3>
                  <div className="preview-image-container">
                    <AsyncProductImage
                      key={`preview-${formState.image || 'default'}`}
                      product={formState}
                      className="preview-main-image"
                      alt="Product preview"
                      onError={(e) => {
                        console.log('Product preview image failed to load, using placeholder');
                      }}
                    />
                  </div>
                  <div className="preview-details">
                    <div className="preview-title">{formState.name || 'Product Name'}</div>
                    <div className="preview-category">
                      <span className="category-icon">{getCategoryIcon(formState)}</span>
                      {formState.type || 'Category'}
                    </div>
                    <div className="preview-price">${formState.price || '0.00'}</div>
                  </div>
                </div>

                <div className="current-image-info">
                  <h4>🖼️ Current Image</h4>
                  <div className="image-path">
                    {formState.image ? (
                      <span className="image-path-text">{formState.image}</span>
                    ) : (
                      <span className="no-image">No image selected</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Panel - Add Form */}
              <div className="edit-right-panel">
                <form onSubmit={handleFormSubmit} className="edit-form">
                  {productSaveError && (
                    <div className="admin-products-save-error" role="alert">
                      <div className="admin-products-save-error__body">
                        <strong className="admin-products-save-error__summary">{productSaveError.summary}</strong>
                        {productSaveError.hint ? (
                          <p className="admin-products-save-error__hint">{productSaveError.hint}</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="admin-products-save-error__dismiss"
                        onClick={() => setProductSaveError(null)}
                        aria-label="Dismiss error"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {/* Form Tabs */}
                  <div className="form-tabs">
                    <button type="button" className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => handleTabClick('basic')}>
                      {getIcon('edit')} Basic Info
                    </button>
                    <button type="button" className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`} onClick={() => handleTabClick('pricing')}>
                      {getIcon('price')} Pricing
                    </button>
                    <button type="button" className={`tab-button ${activeTab === 'details' ? 'active' : ''}`} onClick={() => handleTabClick('details')}>
                      📄 Details
                    </button>
                    {formState.is_bundle && (
                      <button type="button" className={`tab-button ${activeTab === 'bundle' ? 'active' : ''}`} onClick={() => handleTabClick('bundle')}>
                        📦 Bundle Config
                      </button>
                    )}
                    <button type="button" className={`tab-button ${activeTab === 'variants' ? 'active' : ''}`} onClick={() => handleTabClick('variants')}>
                      🎨 Variants
                    </button>
                    <button type="button" className={`tab-button ${activeTab === 'image' ? 'active' : ''}`} onClick={() => handleTabClick('image')}>
                      🖼️ Image
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="tab-content">
                    {/* Basic Information Tab */}
                    <div className={`tab-panel ${activeTab === 'basic' ? 'active' : ''}`} id="basic">
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label className="form-label">Product Name *</label>
                          <input
                            name="name"
                            value={formState.name}
                            onChange={handleInputChange}
                            placeholder="Enter product name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">SKU</label>
                          <div className="input-with-button">
                            <input
                              name="sku"
                              value={formState.sku}
                              onChange={handleInputChange}
                              placeholder="Auto-generated"
                              className="form-input"
                            />
                            <button
                              type="button"
                              onClick={() => setFormState(prev => ({ ...prev, sku: generateProductNumber() }))}
                              className="generate-button"
                            >
                              Generate
                            </button>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Category *</label>
                          <div className="category-selector-group">
                            <select
                              key={`category-select-${categoriesToUse.length}-${categoriesToUse[0]?.name || 'empty'}`}
                              name="type"
                              value={formState.type || 'Book'}
                              onChange={handleInputChange}
                              className="form-select"
                            >
                              {console.log('🎨 ========== RENDERING DROPDOWN ==========') || null}
                              {console.log('🎨 categoriesToUse.length:', categoriesToUse.length) || null}
                              {console.log('🎨 categoriesToUse:', categoriesToUse.map(c => `${c.icon} ${c.name}`)) || null}
                              {console.log('🎨 formState.type:', formState.type) || null}

                              {categoriesToUse.map(cat => (
                                <option key={cat.id} value={cat.name}>
                                  {cat.icon} {cat.name}
                                </option>
                              ))}
                              {/* Add current value if it doesn't exist in categories */}
                              {formState.type && !categoriesToUse.find(cat => cat.name === formState.type) && (
                                <option value={formState.type}>
                                  {formState.type} (Current)
                                </option>
                              )}
                            </select>
                            <a
                              href="/admin/categories"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="manage-categories-link"
                              title="Manage categories in new tab"
                              onClick={(e) => {
                                // Refresh categories when user clicks to manage categories
                                setTimeout(() => fetchCategories(), 1000);
                              }}
                            >
                              + Create Category
                            </a>
                          </div>
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label virtual-product-label">
                            <input
                              type="checkbox"
                              name="shipping_enabled"
                              checked={!formState.shipping_enabled}
                              onChange={(e) => setFormState(prev => ({ ...prev, shipping_enabled: !e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span className="checkbox-text">
                              📱 Virtual/Digital Product (No shipping required)
                            </span>
                          </label>
                          <p className="field-help-text">
                            Check this if the product is digital (e.g., PDF, digital download) and doesn't require physical shipping
                          </p>
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label virtual-product-label">
                            <input
                              type="checkbox"
                              checked={!!formState.is_digital_album}
                              onChange={(e) => handleDigitalAlbumToggle(e.target.checked)}
                              className="form-checkbox"
                            />
                            <span className="checkbox-text">
                              📥 Digital album download (licensed ZIP after purchase)
                            </span>
                          </label>
                          <p className="field-help-text">
                            Upload a master .zip of MP3s; optional cover inside the ZIP. Stored privately — full HST applies.
                          </p>
                        </div>

                        {formState.is_digital_album && (
                          <div className="form-group full-width">
                            <label className="form-label">Master album ZIP *</label>
                            <input
                              ref={digitalZipInputRef}
                              type="file"
                              accept=".zip,application/zip,application/x-zip-compressed"
                              style={{ display: 'none' }}
                              onChange={handleDigitalZipUpload}
                            />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={digitalZipUploading}
                                onClick={() => digitalZipInputRef.current?.click()}
                              >
                                {digitalZipUploading ? 'Uploading…' : formState.digital_bundle_asset_id ? 'Replace ZIP' : 'Upload album ZIP'}
                              </button>
                              {formState.digital_bundle_asset_id && (
                                <span style={{ fontSize: '0.9rem' }}>
                                  ✓ Asset #{formState.digital_bundle_asset_id}
                                  {formState.digital_bundle_asset_name ? ` — ${formState.digital_bundle_asset_name}` : ''}
                                </span>
                              )}
                            </div>
                            {digitalZipUploadMessage && (
                              <p
                                className="field-help-text"
                                style={{ color: digitalZipUploadMessage.type === 'error' ? '#b91c1c' : '#15803d' }}
                              >
                                {digitalZipUploadMessage.text}
                              </p>
                            )}
                          </div>
                        )}

                        {formState.is_digital_album && (
                          <details className="form-group full-width">
                            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Advanced: legacy server folder</summary>
                            <input
                              type="text"
                              name="digital_bundle_slug"
                              value={formState.digital_bundle_slug || ''}
                              onChange={handleInputChange}
                              className="form-input"
                              placeholder="heather-hill-digital"
                              style={{ marginTop: '0.5rem' }}
                            />
                          </details>
                        )}

                        {!formState.is_digital_album && (
                          <div className="form-group full-width">
                            <label className="form-label">Digital alternative (when physical CD is out of stock)</label>
                            <select
                              name="related_digital_product_id"
                              value={formState.related_digital_product_id || ''}
                              onChange={handleInputChange}
                              className="form-input"
                            >
                              <option value="">None — no digital fallback</option>
                              {digitalAlbumOptions.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} (${((p.price_cents || 0) / 100).toFixed(2)})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="form-group full-width">
                          <label className="form-label virtual-product-label">
                            <input
                              type="checkbox"
                              name="is_bundle"
                              checked={formState.is_bundle}
                              onChange={(e) => {
                                setFormState(prev => ({ ...prev, is_bundle: e.target.checked }));
                                if (e.target.checked && activeTab !== 'bundle') {
                                  setActiveTab('bundle');
                                }
                              }}
                              className="form-checkbox"
                            />
                            <span className="checkbox-text">
                              📦 Bundle Product (Customers can select from predefined products)
                            </span>
                          </label>
                          <p className="field-help-text">
                            Check this to create a bundle product where customers can select multiple items from a predefined list with volume-based discounts
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Pricing Tab */}
                    <div className={`tab-panel ${activeTab === 'pricing' ? 'active' : ''}`} id="pricing">
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Price (CAD) *</label>
                          <div className="price-input-container">
                            <input
                              name="price"
                              type="number"
                              value={formState.price}
                              onChange={handleInputChange}
                              placeholder="0 for free"
                              className="form-input price-input"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Inventory *</label>
                          <input
                            name="inventory"
                            type="number"
                            value={formState.inventory}
                            onChange={handleInputChange}
                            placeholder="0"
                            className="form-input"
                            min="0"
                          />
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              name="hide_from_shop"
                              type="checkbox"
                              checked={formState.hide_from_shop || false}
                              onChange={(e) => setFormState(prev => ({ ...prev, hide_from_shop: e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span>🙈 Hide from shop page (direct product link still works)</span>
                          </label>
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              name="checkout_promo"
                              type="checkbox"
                              checked={formState.checkout_promo || false}
                              onChange={(e) => setFormState(prev => ({ ...prev, checkout_promo: e.target.checked }))}
                              className="form-checkbox"
                            />
                            <span>🎁 Add free at checkout (promotional gift when cart has physical items; skipped for digital-only carts)</span>
                          </label>
                          <p className="field-help-text" style={{ marginTop: '4px', marginLeft: '28px' }}>
                            Line price is $0. Skipped if the customer already has this product in the cart. Not used for bundles or variant products.
                          </p>
                        </div>
                      </div>

                      <div className="wholesale-section">
                        <h4>🏢 Wholesale Pricing</h4>
                        <div className="wholesale-grid">
                          {['standard', 'premium', 'volume'].map(tier => (
                            <div key={tier} className="wholesale-tier">
                              <div className="tier-header">
                                <span className="tier-name">{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                                <span className="tier-badge">{tier === 'standard' ? '🥉' : tier === 'premium' ? '🥈' : '🥇'}</span>
                              </div>
                              <div className="tier-inputs">
                                <div className="tier-input-group">
                                  <label>Min Qty:</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={formState.wholesale_pricing[tier].min_qty}
                                    onChange={e => handleWholesaleChange(tier, 'min_qty', parseInt(e.target.value))}
                                    className="tier-input"
                                  />
                                </div>
                                <div className="tier-input-group">
                                  <label>Discount %:</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formState.wholesale_pricing[tier].discount}
                                    onChange={e => handleWholesaleChange(tier, 'discount', parseInt(e.target.value))}
                                    className="tier-input"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Tax Configuration Section */}
                      <div className="tax-config-section">
                        <h4 className="tax-config-section__title">
                          💰 Tax Configuration
                          <span className="tax-config-section__subtitle">
                            (Canadian GST/HST Compliance)
                          </span>
                        </h4>

                        <div className="form-group">
                          <label className="form-label">Tax Type</label>
                          <select
                            name="tax_type"
                            value={formState.tax_type || 'auto'}
                            onChange={handleInputChange}
                            className="form-select"
                            style={{ maxWidth: '400px' }}
                          >
                            <option value="auto">🤖 Auto (CRA Rules) - Books get 5% GST, others use province rate</option>
                            <option value="gst">📚 GST Only (5%) - Point-of-Sale Rebate for Books</option>
                            <option value="hst">🏢 Full HST - Use Provincial Rate (13-15%)</option>
                            <option value="custom">⚙️ Custom Rate - Override with specific percentage</option>
                            <option value="exempt">🆓 Tax Exempt - No tax charged (e.g., educational)</option>
                          </select>
                          <p className="field-help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                            {formState.tax_type === 'auto' && '✓ Recommended: Automatically applies CRA point-of-sale rebate for books'}
                            {formState.tax_type === 'gst' && '📚 Forces 5% GST for this product regardless of category'}
                            {formState.tax_type === 'hst' && '⚠️ Charges full provincial HST rate (use for non-book items)'}
                            {formState.tax_type === 'custom' && '⚙️ Set your own tax rate below (use with caution)'}
                            {formState.tax_type === 'exempt' && '⚠️ No tax will be charged (ensure CRA compliance)'}
                          </p>
                        </div>

                        {formState.tax_type === 'custom' && (
                          <div className="form-group">
                            <label className="form-label">Custom Tax Rate (%)</label>
                            <div className="price-input-container" style={{ maxWidth: '200px' }}>
                              <input
                                type="number"
                                name="tax_rate"
                                value={formState.tax_rate || ''}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="e.g., 5, 13, 15"
                                step="0.01"
                                min="0"
                                max="100"
                              />
                              <span style={{ marginLeft: '0.5rem' }}>%</span>
                            </div>
                            <p className="field-help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-error)' }}>
                              ⚠️ Custom rates override CRA rules. Ensure compliance before using.
                            </p>
                          </div>
                        )}

                        <div className="form-group">
                          <label className="form-label">Tax Configuration Notes (Internal)</label>
                          <textarea
                            name="tax_notes"
                            value={formState.tax_notes || ''}
                            onChange={handleInputChange}
                            className="form-textarea"
                            rows="2"
                            placeholder="e.g., 'Special exemption approved by CRA' or 'Educational material per policy #123'"
                            style={{ fontSize: '0.9rem' }}
                          />
                          <p className="field-help-text" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                            Optional: Document why this tax configuration was chosen (for audit purposes)
                          </p>
                        </div>

                        {/* Tax Preview */}
                        <div className="tax-preview-panel">
                          <h5>🔍 Tax Preview (Ontario Example):</h5>
                          <div className="tax-preview-panel__body">
                            {formState.tax_type === 'auto' && (
                              formState.type && (formState.type.toLowerCase().includes('book') || formState.type.toLowerCase().includes('publication')) ? (
                                <p className="tax-preview-panel__success">
                                  ✓ <strong>Books: 5% GST</strong> (Point-of-Sale Rebate Applied)<br />
                                  <span className="tax-preview-panel__sub">
                                    Customer saves 8% vs full 13% HST in Ontario
                                  </span>
                                </p>
                              ) : (
                                <p className="tax-preview-panel__info">
                                  <strong>Non-Book: 13% HST</strong> (Full provincial rate in Ontario)
                                </p>
                              )
                            )}
                            {formState.tax_type === 'gst' && (
                              <p className="tax-preview-panel__success">
                                ✓ <strong>5% GST Only</strong> (Provincial portion rebated)
                              </p>
                            )}
                            {formState.tax_type === 'hst' && (
                              <p className="tax-preview-panel__info">
                                <strong>13% HST</strong> (Full rate - 5% GST + 8% PST in Ontario)
                              </p>
                            )}
                            {formState.tax_type === 'custom' && formState.tax_rate && (
                              <p className="tax-preview-panel__warning">
                                ⚙️ <strong>{formState.tax_rate}% Custom Rate</strong>
                              </p>
                            )}
                            {formState.tax_type === 'exempt' && (
                              <p className="tax-preview-panel__muted">
                                🆓 <strong>Tax Exempt</strong> (0% - No tax charged)
                              </p>
                            )}
                          </div>
                        </div>

                        {/* CRA Reference Link */}
                        <div className="cra-reference-panel">
                          <strong>📚 CRA Reference:</strong> <a
                            href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses/charge-collect-specific-situations/suppliers-publications.html"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            GST/HST for Suppliers of Publications
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Details Tab */}
                    <div className={`tab-panel ${activeTab === 'details' ? 'active' : ''}`} id="details">
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          name="description"
                          value={formState.description}
                          onChange={handleInputChange}
                          placeholder="Enter detailed product description..."
                          className="form-textarea"
                          rows="6"
                        />
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Author</label>
                          <input
                            name="author"
                            value={formState.author}
                            onChange={handleInputChange}
                            placeholder="Author name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Artist</label>
                          <input
                            name="artist"
                            value={formState.artist}
                            onChange={handleInputChange}
                            placeholder="Artist name"
                            className="form-input"
                          />
                        </div>

                        <div className="form-group full-width">
                          <label className="form-label">Source URL</label>
                          <input
                            name="source_url"
                            value={formState.source_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://www.cranfordpub.com/..."
                            className="form-input"
                            type="url"
                          />
                          <p className="field-help-text">
                            Original URL from cranfordpub.com where this product was imported from
                          </p>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Sample tunes on product page</label>
                        <input
                          name="sample_tunes_limit"
                          type="number"
                          min={1}
                          max={99}
                          value={formState.sample_tunes_limit ?? 9}
                          onChange={handleInputChange}
                          className="form-input"
                        />
                        <p className="field-help-text">
                          Maximum number of &ldquo;Sample Tunes of this Product&rdquo; cards shown (default 9). Use the book index link for the full list.
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Tune index page</label>
                        <div className="input-with-button">
                          <input
                            name="index_page_id"
                            value={formState.index_page_id || ''}
                            onChange={handleInputChange}
                            placeholder="e.g. stubbert3rd-index or https://…/page/stubbert3rd-index"
                            className="form-input"
                            autoComplete="off"
                            style={{ flex: 1, minWidth: 0 }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowTuneIndexPicker(true)}
                            className="generate-button"
                            title="Search book-index and cd-index pages"
                          >
                            Browse…
                          </button>
                        </div>
                        <p className="field-help-text">
                          <code>content_pages.page_id</code> for the book or CD tune index (page type book-index or cd-index).
                          Leave blank to use automatic guesses from SKU and title. Paste a site path or full URL to override, or use Browse to pick a page.
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">🏷️ Tags</label>
                        <div className="input-with-button">
                          <input
                            name="tags"
                            value={formState.tags || ''}
                            onChange={handleInputChange}
                            placeholder="e.g., irish, fiddle, beginner, cape-breton (comma-separated)"
                            className="form-input"
                          />
                          <button
                            type="button"
                            onClick={generateTags}
                            className="generate-button"
                            title="Auto-generate tags from product title, description, category, author, and artist"
                          >
                            ✨ Auto-Populate
                          </button>
                        </div>
                        <p className="field-help-text">
                          Add tags to categorize and relate products. Separate multiple tags with commas. Click "Auto-Populate" to generate tags automatically.
                        </p>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          🔗 Related Products
                          <HelpPopup title="Understanding Product Relationships" position="right">
                            <p><strong>Product relationships help customers discover related items!</strong></p>

                            <div className="tip">
                              💡 <strong>How it works:</strong>
                              <ul>
                                <li>Search and select specific products to recommend</li>
                                <li>Selected products appear first on the product page</li>
                                <li>Optionally include tag-based suggestions</li>
                              </ul>
                            </div>

                            <p><strong>Two Ways to Create Relationships:</strong></p>
                            <ul>
                              <li><strong>Manual Selection:</strong> Use the search box below to pick specific products</li>
                              <li><strong>Automatic Matching:</strong> Enable the checkbox to also show products with matching tags or same category</li>
                            </ul>

                            <div className="example-box">
                              <strong>Example:</strong><br />
                              For "The Cape Breton Fiddle Companion" you might manually select:<br />
                              • "Cape Breton Fiddle Music" (CD)<br />
                              • "Learn Cape Breton Style" (Book)<br />
                              <br />
                              Plus auto-suggestions from products tagged "cape-breton" or "fiddle"
                            </div>

                            <p><strong>Best Practices:</strong></p>
                            <ul>
                              <li>Select 2-4 highly relevant products manually</li>
                              <li>Use tags consistently across products for better auto-matching</li>
                              <li>Related products supersede tag-based suggestions</li>
                            </ul>
                          </HelpPopup>
                        </label>
                        <ProductSearch
                          selectedProductIds={formState.related_products ? formState.related_products.split(',').filter(id => id).map(id => parseInt(id.trim())) : []}
                          onSelectionChange={(productIds) => {
                            setFormState(prev => ({
                              ...prev,
                              related_products: productIds.join(',')
                            }));
                          }}
                          excludeProductId={isEditing ? parseInt(formState.id) : null}
                        />
                        <p className="field-help-text">
                          Select products that are related to this one. They will be displayed on the product detail page.
                        </p>

                        <div className="form-checkbox-group">
                          <p className="form-checkbox-group__title">
                            🎯 Related Products Display Options:
                          </p>

                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              name="related_include_explicit"
                              checked={formState.related_include_explicit !== false}
                              onChange={(e) => setFormState(prev => ({
                                ...prev,
                                related_include_explicit: e.target.checked
                              }))}
                              className="form-checkbox"
                              style={{ marginTop: '3px' }}
                            />
                            <span style={{ marginLeft: '8px' }}>
                              <strong>Show Manually Selected Products</strong><br />
                              <small style={{ color: 'var(--color-text-secondary)' }}>Display the specific products you selected above</small>
                            </span>
                          </label>

                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              name="related_include_tags"
                              checked={formState.related_include_tags !== false}
                              onChange={(e) => setFormState(prev => ({
                                ...prev,
                                related_include_tags: e.target.checked
                              }))}
                              className="form-checkbox"
                              style={{ marginTop: '3px' }}
                            />
                            <span style={{ marginLeft: '8px' }}>
                              <strong>Include Tag-Based Suggestions</strong><br />
                              <small style={{ color: 'var(--color-text-secondary)' }}>Auto-suggest products with matching tags</small>
                            </span>
                          </label>

                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <input
                              type="checkbox"
                              name="related_include_category"
                              checked={formState.related_include_category !== false}
                              onChange={(e) => setFormState(prev => ({
                                ...prev,
                                related_include_category: e.target.checked
                              }))}
                              className="form-checkbox"
                              style={{ marginTop: '3px' }}
                            />
                            <span style={{ marginLeft: '8px' }}>
                              <strong>Include Category-Based Suggestions</strong><br />
                              <small style={{ color: 'var(--color-text-secondary)' }}>Auto-suggest products from the same category</small>
                            </span>
                          </label>

                          <p className="field-help-text" style={{ marginTop: '12px', marginLeft: '0', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                            💡 <strong>Tip:</strong> Enable multiple options to combine different matching strategies. Products appear in priority: Manual → Tags → Category.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bundle Configuration Tab */}
                    {formState.is_bundle && (
                      <div className={`tab-panel ${activeTab === 'bundle' ? 'active' : ''}`} id="bundle">
                        <div className="bundle-config-intro">
                          <strong>📦 Bundle Product Configuration</strong>
                          <p>
                            Configure which products customers can select as part of this bundle, and set volume-based discount rules.
                          </p>
                        </div>

                        {/* Fixed Price Bundle Options */}
                        <div className="form-group bundle-options-panel">
                          <label className="form-label bundle-options-panel__title">
                            🏷️ Fixed Price Bundle Options
                          </label>
                          <p className="field-help-text" style={{ marginBottom: '15px' }}>
                            Define specific combinations with fixed prices (e.g., &quot;Book &amp; CD&quot; for $25). These options are shown as package buttons on the product page.
                            Optional <code>taxAllocations</code> JSON per option (via DB/script) splits Canadian book (5%) vs digital/CD (14%) tax.
                          </p>

                          {(formState.bundle_options || []).map((option, index) => (
                            <div key={index} className="bundle-option-card">
                              <div className="bundle-option-card__header">
                                <strong className="bundle-option-card__title">Option {index + 1}</strong>
                                <button type="button" onClick={() => removeBundleOption(index)} className="bundle-option-remove">
                                  {getIcon('delete')} Remove
                                </button>
                              </div>
                              <div className="form-grid">
                                <div className="form-group">
                                  <label className="form-label">Option Name</label>
                                  <input
                                    value={option.name}
                                    onChange={(e) => updateBundleOption(index, 'name', e.target.value)}
                                    placeholder="e.g., Book & CD Bundle"
                                    className="form-input"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Fixed Price ($)</label>
                                  <input
                                    type="number"
                                    value={option.price}
                                    onChange={(e) => updateBundleOption(index, 'price', e.target.value)}
                                    placeholder="24.95"
                                    className="form-input"
                                    step="0.01"
                                  />
                                </div>
                              </div>
                              <div className="form-group">
                                <label className="form-label">Included Product IDs</label>
                                <input
                                  value={option.items ? option.items.join(',') : ''}
                                  onChange={(e) => updateBundleOption(index, 'items', e.target.value.split(',').map(s => s.trim()))}
                                  placeholder="e.g., 703, 704"
                                  className="form-input"
                                />
                                <small className="field-help-text">Comma separated IDs of products included in this option.</small>
                              </div>
                            </div>
                          ))}

                          <button type="button" onClick={addBundleOption} className="admin-products-secondary-button">
                            + Add Fixed Price Option
                          </button>
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Bundle Products Selection
                            <HelpPopup title="Bundle Products" position="right">
                              <p><strong>How Bundle Products Work:</strong></p>
                              <p>Select which products customers can choose from when purchasing this bundle.</p>

                              <div className="tip">
                                <strong>Fixed Price Options:</strong><br />
                                You can also define specific combinations with fixed prices below (e.g., "Book & CD" for $24.95).
                              </div>

                              <div className="example-box">
                                <strong>Example:</strong><br />
                                Create a "Celtic Music Collection" bundle where customers can select 5+ books from your catalog of Irish, Scottish, and Cape Breton music books.
                              </div>

                              <p><strong>Pricing:</strong></p>
                              <ul>
                                <li>Set minimum quantity (e.g., 5 books)</li>
                                <li>Set discount percentage (e.g., 30% off)</li>
                                <li>Customers select products and get the discount applied</li>
                              </ul>
                            </HelpPopup>
                          </label>

                          <button
                            type="button"
                            onClick={() => setShowBundlePickerModal(true)}
                            className="bundle-picker-open-button"
                            style={{
                              width: '100%',
                              padding: '16px 24px',
                              background: 'var(--color-primary)',
                              color: 'var(--color-background)',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '10px',
                              transition: 'all 0.2s',
                              marginBottom: '15px'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = 'var(--color-primary-dark)';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = 'var(--color-primary)';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            <span style={{ fontSize: '20px' }}>🔍</span>
                            <span>
                              {formState.bundle_products && formState.bundle_products.split(',').filter(id => id).length > 0
                                ? `Edit Selected Products (${formState.bundle_products.split(',').filter(id => id).length} selected)`
                                : 'Select Products for Bundle'}
                            </span>
                          </button>

                          {formState.bundle_products && formState.bundle_products.split(',').filter(id => id).length > 0 && (() => {
                            const selectedIds = formState.bundle_products.split(',').filter(id => id);
                            const selectedProducts = selectedIds.map(id =>
                              products.find(p => p.id === parseInt(id))
                            ).filter(p => p);

                            return (
                              <div className="bundle-selected-summary">
                                <strong>{selectedIds.length} products selected</strong>
                                <div className="bundle-selected-summary__meta">
                                  {selectedProducts.length > 0 ? (
                                    <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                                      {selectedProducts.slice(0, 10).map(product => (
                                        <li key={product.id} style={{ marginBottom: '3px' }}>
                                          {product.name} <span className="bundle-selected-summary__id">(ID: {product.id})</span>
                                        </li>
                                      ))}
                                      {selectedProducts.length > 10 && (
                                        <li className="bundle-selected-summary__id" style={{ fontStyle: 'italic' }}>
                                          and {selectedProducts.length - 10} more...
                                        </li>
                                      )}
                                    </ul>
                                  ) : (
                                    <p style={{ margin: '5px 0 0 0' }}>
                                      Product IDs: {selectedIds.slice(0, 10).join(', ')}
                                      {selectedIds.length > 10 && '...'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          <p className="field-help-text">
                            Click the button above to open a searchable modal where you can select multiple products for this bundle. Customers will see these products with checkboxes on the bundle product page.
                          </p>
                        </div>

                        <div className="form-grid" style={{ marginTop: '25px' }}>
                          <div className="form-group">
                            <label className="form-label">Minimum Quantity</label>
                            <input
                              type="number"
                              name="bundle_min_quantity"
                              value={formState.bundle_min_quantity}
                              onChange={handleInputChange}
                              className="form-input"
                              placeholder="5"
                              min="1"
                              required
                            />
                            <p className="field-help-text">
                              Minimum number of products customer must select to qualify for the bundle discount
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Bundle Discount (%)</label>
                            <div className="price-input-container">
                              <input
                                type="number"
                                name="bundle_discount"
                                value={formState.bundle_discount}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="30"
                                min="0"
                                max="100"
                                step="0.01"
                                required
                              />
                              <span style={{ marginLeft: '8px' }}>%</span>
                            </div>
                            <p className="field-help-text">
                              Discount percentage applied when minimum quantity is reached (leave empty if using fixed price). Can use decimals like 23.85%
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Fixed Bundle Price ($)</label>
                            <div className="price-input-container">
                              <span style={{ marginRight: '8px' }}>$</span>
                              <input
                                type="number"
                                name="bundle_fixed_price_cents"
                                value={formState.bundle_fixed_price_cents ? (formState.bundle_fixed_price_cents / 100).toFixed(2) : ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setFormState(prev => ({
                                    ...prev,
                                    bundle_fixed_price_cents: value ? Math.round(parseFloat(value) * 100) : null
                                  }));
                                }}
                                className="form-input"
                                placeholder="24.95"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <p className="field-help-text">
                              Fixed price for the entire bundle (e.g., $24.95). If set, this overrides the percentage discount. Leave empty to use percentage discount.
                            </p>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Maximum Selections</label>
                            <input
                              type="number"
                              name="bundle_max_selections"
                              value={formState.bundle_max_selections}
                              onChange={handleInputChange}
                              className="form-input"
                              placeholder="10"
                              min="1"
                              required
                            />
                            <p className="field-help-text">
                              Maximum number of products customer can select (optional limit). If same as minimum quantity, products will be auto-selected and locked.
                            </p>
                          </div>
                        </div>



                        {/* Bundle Preview */}
                        <div className="bundle-preview-panel">
                          <h4>📊 Bundle Preview</h4>
                          <div className="bundle-preview-panel__body">
                            <p style={{ margin: '5px 0' }}>
                              <strong>Products available:</strong> {formState.bundle_products ? formState.bundle_products.split(',').filter(id => id).length : 0}
                            </p>
                            <p style={{ margin: '5px 0' }}>
                              <strong>Discount rule:</strong> Select {formState.bundle_min_quantity}+ products to get {formState.bundle_discount}% off
                            </p>
                            <p style={{ margin: '5px 0' }}>
                              <strong>Selection limit:</strong> Up to {formState.bundle_max_selections} products
                            </p>
                            {formState.bundle_products && formState.bundle_products.split(',').filter(id => id).length > 0 && (
                              <div className="status-banner--success">
                                ✓ Bundle is ready! Customers can select from {formState.bundle_products.split(',').filter(id => id).length} products.
                              </div>
                            )}
                            {(!formState.bundle_products || formState.bundle_products.split(',').filter(id => id).length === 0) && (
                              <div className="status-banner--warning">
                                ⚠️ Please select at least one product for the bundle.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Variants Tab */}
                    <div className={`tab-panel ${activeTab === 'variants' ? 'active' : ''}`} id="variants">
                      <VariantConfigurator
                        productData={formState}
                        onChange={(variantData) => {
                          setFormState(prev => ({
                            ...prev,
                            ...variantData
                          }));
                        }}
                      />
                    </div>

                    {/* Image Tab */}
                    <div className={`tab-panel ${activeTab === 'image' ? 'active' : ''}`} id="image">
                      <div className="image-selection-section">
                        {/* Asset Search Section */}
                        <div className="asset-search-section">
                          <label className="form-label">
                            🔍 Search Database Assets
                            <HelpPopup title="Product Image Selection" position="right">
                              <p><strong>Three ways to add product images:</strong></p>

                              <p><strong>1. Search Database Assets (Recommended)</strong></p>
                              <ul>
                                <li>Search your uploaded assets by name or tags</li>
                                <li>Click to select any image</li>
                                <li>Images are stored centrally and can be reused</li>
                              </ul>

                              <p><strong>2. Select from Legacy Images</strong></p>
                              <ul>
                                <li>Browse images organized by category</li>
                                <li>Legacy system for backwards compatibility</li>
                              </ul>

                              <p><strong>3. Upload Custom Image</strong></p>
                              <ul>
                                <li>Upload a new image directly</li>
                                <li>Automatically added to asset database</li>
                              </ul>

                              <div className="tip">
                                💡 <strong>Best Practice:</strong> Use Database Assets for better organization and reusability!
                              </div>

                              <div className="warning">
                                ⚠️ Images should be high quality (at least 800x800px) for best display on product pages
                              </div>
                            </HelpPopup>
                          </label>
                          <div className="asset-search-container">
                            <input
                              type="text"
                              placeholder="Search for images by name, description, or tags..."
                              value={assetSearchTerm}
                              onChange={handleAssetSearchChange}
                              className="form-input asset-search-input"
                            />
                            {assetSearchLoading && <div className="search-loading">Searching...</div>}
                          </div>
                          {/* Asset Grid */}
                          <div className="asset-grid">
                            {assets.map(asset => (
                              <div
                                key={asset.id}
                                className={`asset-item ${formState.image === `/api/assets/${asset.id}/download` ? 'selected' : ''}`}
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <img
                                  src={`/api/assets/${asset.id}/thumbnail`}
                                  alt={asset.alt_text || asset.original_filename}
                                  className="asset-thumbnail"
                                />
                                <div className="asset-info">
                                  <div className="asset-name">{asset.original_filename}</div>
                                  {asset.description && (
                                    <div className="asset-description">{asset.description}</div>
                                  )}
                                  {asset.category && (
                                    <div className="asset-category">{asset.category}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                            {!assetSearchLoading && assets.length === 0 && (
                              <div className="no-assets">No assets found. Try a different search term.</div>
                            )}
                          </div>
                        </div>

                        {/* Legacy Image Selector and Upload Button Row */}
                        <div className="image-selection-row">
                          <div className="image-selector">
                            <label className="form-label">📁 Select from Legacy Images</label>
                            <select
                              name="image"
                              value={formState.image}
                              onChange={handleImageSelect}
                              className="form-select image-select"
                            >
                              <option value="">-- Choose an image --</option>
                              <optgroup label="📚 Cape Breton Books">
                                {availableImages.filter(img => img.startsWith('cape_breton/')).map(img => (
                                  <option key={img} value={img}>{img.replace('cape_breton/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🍀 Irish Books">
                                {availableImages.filter(img => img.startsWith('irish/')).map(img => (
                                  <option key={img} value={img}>{img.replace('irish/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🏴󠁧󠁢󠁳󠁣󠁴󠁿 Scottish Books">
                                {availableImages.filter(img => img.startsWith('scottish/')).map(img => (
                                  <option key={img} value={img}>{img.replace('scottish/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🍁 Canadian Books">
                                {availableImages.filter(img => img.startsWith('canadian/')).map(img => (
                                  <option key={img} value={img}>{img.replace('canadian/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="🎵 Pipe Music">
                                {availableImages.filter(img => img.startsWith('pipe_music/')).map(img => (
                                  <option key={img} value={img}>{img.replace('pipe_music/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="💿 CDs">
                                {availableImages.filter(img => img.startsWith('cds/')).map(img => (
                                  <option key={img} value={img}>{img.replace('cds/', '')}</option>
                                ))}
                              </optgroup>
                              <optgroup label="📦 Other">
                                {availableImages.filter(img => !img.includes('/')).map(img => (
                                  <option key={img} value={img}>{img}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          <div className="image-upload-section">
                            <label htmlFor="image-upload" className="image-upload-label">
                              {uploadingImage ? '⏳ Uploading...' : '📁 Upload Custom Image'}
                            </label>
                            <input
                              ref={fileInputRef}
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              disabled={uploadingImage}
                              style={{ display: 'none' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="form-actions">
                    <button type="button" className="cancel-button" onClick={() => { setProductSaveError(null); setIsAdding(false); }} disabled={isSavingProduct}>
                      Cancel
                    </button>
                    <button type="submit" className="save-button" disabled={isSavingProduct}>
                      {isSavingProduct ? '⏳ Saving...' : '➕ Add Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="product-list">
        <div className="products-grid">
          {filteredProducts.map(product => {
            const isSelected = selectedProducts.includes(product.id);
            return (
              <div key={product.id} className={`product-card-compact ${isSelected ? 'selected' : ''}`}>
                <div className="product-select-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectProduct(product.id)}
                    onClick={(e) => e.stopPropagation()}
                    title="Select product"
                  />
                </div>

                <div className="product-thumbnail">
                  <AsyncProductImage
                    product={product}
                    alt={product.name}
                    onError={e => {
                      console.log('Product image failed to load, using placeholder');
                    }}
                  />
                </div>

                <div className="product-info">
                  <div className="product-title">
                    <span className="category-icon" title={product.category || product.type || 'Bundle'}>
                      {getCategoryIcon(product)}
                    </span>
                    <span className="product-name" title={product.name}>
                      {product.name.length > 40 ? product.name.substring(0, 40) + '...' : product.name}
                    </span>
                  </div>

                  <div className="product-details">
                    {product.sku && <span className="sku">SKU: {product.sku}</span>}
                    <span className="price">${(product.price_cents / 100).toFixed(2)}</span>
                    <span className="stock">Stock: {product.inventory || 0}</span>
                  </div>

                  <div className="product-category">
                    {product.category || product.type || 'Bundle'}
                    {(product.hide_from_shop === 1 || product.hide_from_shop === true) && (
                      <span className="product-badge product-badge-hidden-shop" title="Hidden from /shop"> 🙈 Shop hidden</span>
                    )}
                  </div>
                </div>

                <div className="product-actions">
                  <button
                    onClick={() => window.open(`/product/${product.id}`, '_blank')}
                    className="preview-button"
                    title="Preview product"
                  >
                    {getIcon('eye')}
                  </button>
                  <button
                    onClick={() => handleExportProduct(product.id)}
                    className="export-button"
                    title="Export product as ZIP file"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {getIcon('download')}
                  </button>
                  <button
                    onClick={() => handleEdit(product)}
                    className="edit-button"
                    title="Edit product"
                  >
                    {getIcon('edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="delete-button"
                    title="Delete product"
                  >
                    {getIcon('delete')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filteredProducts.length === 0 && (
          <div className="no-products-message">
            {searchTerm ? (
              <>
                <p>No products found matching "{searchTerm}"</p>
                <button onClick={() => setSearchTerm('')} className="clear-search-button">
                  Clear Search
                </button>
              </>
            ) : (
              <p>No products available. Click "Add New Product" to get started.</p>
            )}
          </div>
        )}

        {/* Import Progress Modal */}
        {(importProgress.isLoading || importProgress.step === 'success' || importProgress.step === 'error') && (
          <div className="edit-modal-overlay">
            <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
              <div className="edit-modal-header">
                <h2>
                  {importProgress.step === 'success' ? '✅ ' : importProgress.step === 'error' ? '❌ ' : '⏳ '}
                  {importProgress.step === 'uploading' ? 'Uploading ZIP File' :
                    importProgress.step === 'processing' ? 'Processing Import' :
                      importProgress.step === 'finalizing' ? 'Finalizing Import' :
                        importProgress.step === 'updating' ? 'Updating Product' :
                          importProgress.step === 'creating' ? 'Creating Product' :
                            importProgress.step === 'success' ? 'Import Successful' :
                              'Import Error'}
                </h2>
                {!importProgress.isLoading && (
                  <button
                    className="close-button"
                    onClick={() => setImportProgress({ isLoading: false, step: '', message: '', error: null, productData: null, existingProduct: null })}
                    title="Close"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="edit-modal-content" style={{ padding: '30px' }}>
                {importProgress.isLoading ? (
                  <div style={{ textAlign: 'center' }}>
                    <LoadingSpinner size="medium" message={importProgress.message || 'Processing...'} />
                    <p style={{
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      marginTop: '1rem'
                    }}>
                      Please wait while we import your product
                    </p>
                  </div>
                ) : importProgress.step === 'success' ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
                    <p style={{
                      fontSize: '16px',
                      color: 'var(--color-success)',
                      fontWeight: '600',
                      marginBottom: '15px'
                    }}>
                      {importProgress.message || 'Product imported successfully!'}
                    </p>
                    {importProgress.productData && (
                      <div style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        padding: '15px',
                        marginTop: '20px',
                        textAlign: 'left'
                      }}>
                        <div style={{ color: 'var(--color-text-primary)', fontWeight: '600', marginBottom: '8px' }}>
                          {importProgress.productData.name}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                          SKU: {importProgress.productData.sku || 'N/A'}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => setImportProgress({ isLoading: false, step: '', message: '', error: null, productData: null, existingProduct: null })}
                      className="form-submit-button"
                      style={{
                        width: '100%',
                        marginTop: '25px',
                        background: 'var(--color-success)'
                      }}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
                    <p style={{
                      fontSize: '16px',
                      color: 'var(--color-error)',
                      fontWeight: '600',
                      marginBottom: '15px'
                    }}>
                      Import Failed
                    </p>
                    <div style={{
                      backgroundColor: 'var(--color-surface)',
                      border: '1px solid var(--color-error)',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '20px',
                      textAlign: 'left'
                    }}>
                      <p style={{
                        fontSize: '14px',
                        color: 'var(--color-text-primary)',
                        margin: 0
                      }}>
                        {importProgress.error || 'An unknown error occurred'}
                      </p>
                    </div>
                    <button
                      onClick={() => setImportProgress({ isLoading: false, step: '', message: '', error: null, productData: null, existingProduct: null })}
                      className="form-submit-button"
                      style={{
                        width: '100%',
                        background: 'var(--color-error)'
                      }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Warning Modal */}
        {showDuplicateWarning && duplicateProducts.length > 0 && (
          <div className="edit-modal-overlay">
            <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <div className="edit-modal-header">
                <h2>⚠️ Possible Duplicate Products Detected</h2>
                <button
                  className="close-button"
                  onClick={handleCancelDuplicateWarning}
                  title="Cancel"
                >
                  ✕
                </button>
              </div>

              <div className="edit-modal-content" style={{ padding: '30px' }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '25px',
                  lineHeight: '1.6'
                }}>
                  We found <strong style={{ color: 'var(--color-warning)' }}>{duplicateProducts.length}</strong> product(s) that may be duplicates of the one you're trying to save.
                  Please review and confirm if you want to proceed.
                </p>

                {/* Product being saved */}
                <div style={{
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  border: '2px solid var(--color-warning)',
                  borderRadius: '8px',
                  padding: '18px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '18px' }}>💾</span>
                    <strong style={{
                      fontSize: '14px',
                      color: 'var(--color-text-primary)'
                    }}>
                      Product You're Saving
                    </strong>
                  </div>
                  <div style={{ paddingLeft: '28px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Name:</strong> {formState.name}
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>SKU:</strong> {formState.sku || 'Auto-generated'}
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Price:</strong> ${formState.price || '0.00'}
                    </div>
                    <div>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Category:</strong> {formState.type}
                    </div>
                  </div>
                </div>

                {/* Possible duplicates */}
                <div style={{ marginBottom: '25px' }}>
                  <strong style={{
                    fontSize: '14px',
                    color: 'var(--color-text-primary)',
                    display: 'block',
                    marginBottom: '12px'
                  }}>
                    Possible Duplicates Found:
                  </strong>

                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {duplicateProducts.map((dup, index) => (
                      <div key={dup.product.id} style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: 'var(--color-text-primary)',
                              marginBottom: '8px'
                            }}>
                              {dup.product.name}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                              <strong>SKU:</strong> {dup.product.sku || 'N/A'} | <strong>Price:</strong> ${((dup.product.price_cents || 0) / 100).toFixed(2)} | <strong>Category:</strong> {dup.product.category || 'N/A'}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: 'var(--color-warning)',
                              marginTop: '8px'
                            }}>
                              <strong>Match reasons:</strong> {dup.reasons.join(', ')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  border: '1px solid var(--color-primary)',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '25px'
                }}>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                    💡 <strong style={{ color: 'var(--color-text-primary)' }}>Tip:</strong> Review the products above carefully. If this is truly a new product, click "Save Anyway". Otherwise, cancel and edit the existing product instead.
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleCancelDuplicateWarning}
                    className="cancel-button"
                    style={{ flex: 1 }}
                  >
                    ✕ Cancel
                  </button>
                  <button
                    onClick={handleProceedWithSave}
                    className="form-submit-button"
                    disabled={isSavingProduct}
                    style={{
                      flex: 1,
                      background: 'var(--color-warning)',
                      opacity: isSavingProduct ? 0.75 : 1
                    }}
                  >
                    {isSavingProduct ? 'Saving...' : '✓ Save Anyway'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conflict Resolution Modal */}
        {showConflictModal && importProgress.existingProduct && importProgress.productData && (
          <div className="edit-modal-overlay">
            <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
              <div className="edit-modal-header">
                <h2>⚠️ Product Conflict Detected</h2>
                <button
                  className="close-button"
                  onClick={() => {
                    setShowConflictModal(false);
                    setImportProgress({ isLoading: false, step: '', message: '', error: null, productData: null, existingProduct: null });
                    pendingImportFile.current = null;
                  }}
                  title="Cancel"
                >
                  ✕
                </button>
              </div>

              <div className="edit-modal-content" style={{ padding: '30px' }}>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '25px',
                  lineHeight: '1.6'
                }}>
                  A product with SKU <strong style={{ color: 'var(--color-text-primary)' }}>"{importProgress.productData.sku}"</strong> already exists.
                  Choose how to proceed:
                </p>

                {/* Existing Product Info */}
                <div style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  padding: '18px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '18px' }}>📦</span>
                    <strong style={{
                      fontSize: '14px',
                      color: 'var(--color-text-primary)'
                    }}>
                      Existing Product
                    </strong>
                  </div>
                  <div style={{ paddingLeft: '28px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Name:</strong> {importProgress.existingProduct.name}
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>SKU:</strong> {importProgress.existingProduct.sku}
                    </div>
                    <div>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Price:</strong> ${((importProgress.existingProduct.price_cents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* New Product Info */}
                <div style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  padding: '18px',
                  marginBottom: '25px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '18px' }}>🆕</span>
                    <strong style={{
                      fontSize: '14px',
                      color: 'var(--color-text-primary)'
                    }}>
                      Imported Product
                    </strong>
                  </div>
                  <div style={{ paddingLeft: '28px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Name:</strong> {importProgress.productData.name || 'Unnamed Product'}
                    </div>
                    <div style={{ marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>SKU:</strong> {importProgress.productData.sku}
                    </div>
                    <div>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Price:</strong> ${((importProgress.productData.price_cents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Options */}
                <div style={{ marginBottom: '25px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '18px',
                    border: '2px solid var(--color-primary)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: 'rgba(74, 144, 226, 0.1)',
                    marginBottom: '15px'
                  }}>
                    <input
                      type="radio"
                      name="conflictResolution"
                      value="update"
                      checked={conflictResolution === 'update'}
                      onChange={(e) => setConflictResolution(e.target.value)}
                      style={{ marginRight: '12px', marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '6px', color: 'var(--color-text-primary)' }}>
                        Update Existing Product ✏️
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                        Replace the existing product's data with the imported product data. The existing product ID and SKU will be preserved.
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '18px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: 'var(--color-surface)'
                  }}>
                    <input
                      type="radio"
                      name="conflictResolution"
                      value="create_new"
                      checked={conflictResolution === 'create_new'}
                      onChange={(e) => setConflictResolution(e.target.value)}
                      style={{ marginRight: '12px', marginTop: '3px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '6px', color: 'var(--color-text-primary)' }}>
                        Create New Product 🆕
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                        Create a new product with a different SKU. The SKU will be automatically changed to <strong style={{ color: 'var(--color-text-primary)' }}>"{importProgress.productData.sku}-IMP"</strong> to differentiate it from the existing product. The existing product will remain unchanged.
                      </div>
                    </div>
                  </label>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setShowConflictModal(false);
                      setImportProgress({ isLoading: false, step: '', message: '', error: null, productData: null, existingProduct: null });
                      setConflictResolution(null);
                      pendingImportFile.current = null;
                    }}
                    className="cancel-button"
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => conflictResolution && handleConflictResolution(conflictResolution)}
                    disabled={!conflictResolution}
                    className="form-submit-button"
                    style={{
                      flex: 1,
                      background: conflictResolution ? 'var(--color-primary)' : 'var(--color-border)',
                      cursor: conflictResolution ? 'pointer' : 'not-allowed',
                      opacity: conflictResolution ? 1 : 0.6
                    }}
                  >
                    {conflictResolution === 'update' ? 'Update Product' : conflictResolution === 'create_new' ? 'Create New Product' : 'Choose Option'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bundle Product Picker Modal */}
        <BundleProductPicker
          isOpen={showBundlePickerModal}
          onClose={() => setShowBundlePickerModal(false)}
          selectedProductIds={formState.bundle_products ? formState.bundle_products.split(',').filter(id => id).map(id => parseInt(id)) : []}
          onSelectionChange={(selectedProductIds) => {
            console.log('📦 Bundle products selected:', selectedProductIds);
            setFormState(prev => ({
              ...prev,
              bundle_products: selectedProductIds.join(',')
            }));
            setShowBundlePickerModal(false);
          }}
          excludeProductId={formState.id ? parseInt(formState.id) : null}
        />

        <TuneIndexPickerModal
          isOpen={showTuneIndexPicker}
          onClose={() => setShowTuneIndexPicker(false)}
          onSelect={(page) => {
            setFormState((prev) => ({
              ...prev,
              index_page_id: page && page.page_id != null ? String(page.page_id).trim() : '',
            }));
          }}
        />
      </div>

    </div>
  );
};

export default AdminProducts; 